import { useState, useRef, useMemo, useEffect } from "react";
import { getWorkspaceTimeZone } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileFilterSheet, MobileFilterField, MobileRegionPicker } from "@/components/mobile/mobile-filter-sheet";
import { useLocation, useSearch } from "wouter";
import { useQuery, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { useSendEmailWithPreflight } from "@/hooks/use-pending-email-drafts";
import { useVirtualizer } from "@tanstack/react-virtual";
import confetti from "canvas-confetti";
import type { UserProfile, Region, ClientTypeRecord, Talent, LateFeeTier, LateFeeStateException } from "@shared/schema";
import { useForm } from "react-hook-form";
import { Badge } from "@/components/ui/badge";
import { SpreadsheetSkeleton } from "@/components/spreadsheet-skeleton";
import { EmptyState } from "@/components/empty-state";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceTextarea as Textarea, VoiceButton as VoiceToTextButton } from "@/components/voice-field";
import { RichTextEditor, type RichTextEditorHandle } from "@/components/rich-text-editor";
import { EmailVariablesSelector } from "@/components/email-variables-selector";
import { EmailAttachmentsField, type StagedAttachment } from "@/components/email-attachments-field";
import { CcEmailInput } from "@/components/cc-email-input";
import { InvoicePaymentDialog } from "@/components/invoice-payment-dialog";
import { InvoiceStatusDot } from "@/components/invoice-status-dot";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { InvoiceView } from "@/components/invoice/invoice-view";
import { LogEventDialog } from "@/components/log-event-dialog";
import { TutorialHint } from "@/components/tutorial-hint";
import { EmailComposerModal } from "@/components/email-composer-modal";
import { PaymentEmailModal } from "@/components/payment-email-modal";
import { FileText, Loader2, Eye, Printer, Mail, Send, Trash2, XCircle, Pencil, ArrowUpDown, ArrowUp, ArrowDown, Plus, Edit, Search, X, ChevronDown, Undo2, Redo2, Phone, Bell, Download, Clock } from "lucide-react";
import { ScheduledEmailDialog } from "@/components/scheduled-email-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn, normalizeSearchStr } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import { apiRequest, queryClient, withCsrfHeader, sanitizeErrorBody } from "@/lib/queryClient";
import { BulkActionsDropdown } from "@/components/bulk-actions-dropdown";
import { SavedViewsTabStrip } from "@/components/saved-views-tab-strip";
import type { Invoice, NlFilterSpec, UserRole } from "@shared/schema";
import { initials as bsInitials, avatarSwatch as bsAvatarSwatch } from "@/components/backstage/lib";
import { SmartSearchBar } from "@/components/smart-search-bar";
import { RecordSuggestionChips } from "@/components/record-suggestion-chips";
import { useGlobalSearchSuggestions } from "@/hooks/use-global-search-suggestions";
import { ExpandableText } from "@/components/ui/expandable-text";

interface InvoicesPageProps {
  effectiveRole?: UserRole;
  effectiveUserId?: string;
}

const INVOICE_EXPORT_FIELDS = [
  { key: "id", label: "Invoice ID" },
  { key: "invoiceNumber", label: "Invoice Number" },
  { key: "clientId", label: "Client ID" },
  { key: "accountName", label: "Account Name" },
  { key: "accountAddress", label: "Account Address" },
  { key: "amount", label: "Amount" },
  { key: "dateOfEvent", label: "Date of Event" },
  { key: "invoiceDate", label: "Invoice Date" },
  { key: "bookingDetails", label: "Booking Details" },
  { key: "billingNotes", label: "Billing Notes" },
  { key: "billingContactName", label: "Billing Contact Name" },
  { key: "billingPhoneNumber", label: "Billing Phone Number" },
  { key: "billingEmail", label: "Billing Email" },
  { key: "billingLastContact", label: "Billing Last Contact" },
  { key: "status", label: "Status" },
  { key: "paid", label: "Paid" },
  { key: "lateFee", label: "Late Fee" },
  { key: "lateFeeManualOverride", label: "Late Fee Manual Override" },
  { key: "datePaid", label: "Date Paid" },
  { key: "paymentType", label: "Payment Type" },
  { key: "paymentNotes", label: "Payment Notes" },
  { key: "cancellationReason", label: "Cancellation Reason" },
  { key: "rep", label: "Rep" },
  { key: "talentId", label: "Talent ID" },
  { key: "talentName", label: "Talent (Name)" },
  { key: "regionId", label: "Region ID" },
  { key: "regionName", label: "Region (Name)" },
  { key: "createdBy", label: "Created By" },
];

// Renders email-type activities so the timeline doesn't dump the full body
// inline. Splits the notes string the inbound-email-processor writes
// ("<header>\nSubject: <subject>\n\n<body>") into header + subject + body.
// Header and subject are always visible; body is gated behind a
// "Show email body" toggle so the activity log stays scannable.
function EmailActivityBody({ notes }: { notes: string }) {
  const [showBody, setShowBody] = useState(false);
  // Reset the expanded state if the notes prop changes — guards against
  // expanded state bleeding into a different activity row when React
  // reuses an instance (e.g. when a row's id is missing and we fall back
  // to the array index as the key).
  useEffect(() => {
    setShowBody(false);
  }, [notes]);
  const text = notes || "";
  const subjectIdx = text.indexOf("Subject:");
  const header = subjectIdx > 0 ? text.slice(0, subjectIdx).trim() : "";
  let subjectLine = "";
  let body = "";
  if (subjectIdx >= 0) {
    const afterSubject = text.slice(subjectIdx);
    const blankLineIdx = afterSubject.indexOf("\n\n");
    if (blankLineIdx >= 0) {
      subjectLine = afterSubject.slice(0, blankLineIdx).trim();
      body = afterSubject.slice(blankLineIdx + 2).trim();
    } else {
      subjectLine = afterSubject.trim();
    }
  } else {
    subjectLine = text.trim();
  }
  return (
    <div className="text-sm space-y-1">
      {header && <p className="text-muted-foreground break-words">{header}</p>}
      {subjectLine && <p className="font-medium break-words">{subjectLine}</p>}
      {body && (
        <>
          {showBody && (
            <pre
              className="mt-2 whitespace-pre-wrap break-words text-sm bg-muted/30 rounded-md p-3 font-sans"
              data-testid="text-email-body"
            >
              {body}
            </pre>
          )}
          <button
            type="button"
            onClick={() => setShowBody((v) => !v)}
            className="text-xs text-primary mt-1 hover:underline focus:outline-none"
            data-testid="button-toggle-email-body"
          >
            {showBody ? "Hide email body" : "Show email body"}
          </button>
        </>
      )}
    </div>
  );
}

interface EditInvoiceForm {
  accountName: string;
  accountAddress: string;
  eventRate: string;
  discount: string;
  invoiceDate: string;
  dateOfEvent: string;
  bookingDetails: string;
  billingContactName: string;
  billingPhoneNumber: string;
  billingEmail: string;
  status: string;
  lateFee: string;
  lateFeeManualOverride: boolean;
  paymentTerms: string;
  cancellationReason: string;
  datePaid: string;
}

type SortField = "invoiceNumber" | "accountName" | "dateOfEvent" | "status" | "eventRate" | "lateFee" | "rep" | "paid";
type SortDirection = "asc" | "desc";

// Bulk delete is a soft delete and can be reversed via the per-record
// restore endpoint, so it can live on an undo/redo stack.
type InvoiceUndoAction = { type: "bulkDelete"; invoiceIds: string[] };

export default function InvoicesPage({ effectiveRole, effectiveUserId }: InvoicesPageProps) {
  const isMobile = useIsMobile();
  const [, navigateLocation] = useLocation();
  const search = useSearch();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  // Server-side cursor pagination + virtualization replaces the legacy
  // visibleInvoicesCount + JS slice model. The IntersectionObserver
  // attached to loadMoreSentinelRef drives fetchNextPage, the row
  // virtualizer below trims the DOM cost.
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [smartSpec, setSmartSpec] = useState<NlFilterSpec | null>(null);
  const [smartEnabled, setSmartEnabled] = useState(false);
  const [smartHydrated, setSmartHydrated] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("invoices-selected-regions");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {}
    return [];
  });
  const [selectedClientType, setSelectedClientType] = useState<string>(() => {
    // null in storage means "never been set" → use sentinel so default logic can apply DB preference.
    // "" means user explicitly chose "All Client Types".
    const saved = localStorage.getItem("invoices-selected-client-type");
    return saved !== null ? saved : "__unset__";
  });
  // Task #324 — "__unset__" sentinel resolves to the workspace default talent
  // (or the lone talent in single-talent workspaces) once talent loads. An
  // explicit user choice is tracked so an auto-resolved default is never
  // persisted as if the user picked it.
  const [selectedTalentId, setSelectedTalentId] = useState<string>(() => {
    const saved = localStorage.getItem("invoices-selected-talent");
    return saved !== null ? saved : "__unset__";
  });
  const talentPrefExplicitRef = useRef<boolean>(
    localStorage.getItem("invoices-selected-talent") !== null,
  );
  const [regionSearch, setRegionSearch] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [emailPdfPopoverOpen, setEmailPdfPopoverOpen] = useState(false);
  const [emailPdfAddress, setEmailPdfAddress] = useState("");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isComposeEmailOpen, setIsComposeEmailOpen] = useState(false);
  const [isComposeEmailScheduleOpen, setIsComposeEmailScheduleOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailAttachments, setEmailAttachments] = useState<StagedAttachment[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [invoiceToMarkPaid, setInvoiceToMarkPaid] = useState<Invoice | null>(null);
  const [paymentEmailModalOpen, setPaymentEmailModalOpen] = useState(false);
  const [justPaidInvoice, setJustPaidInvoice] = useState<Invoice | null>(null);
  const justPaidInvoiceRef = useRef<Invoice | null>(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
  const [invoiceToCancel, setInvoiceToCancel] = useState<Invoice | null>(null);
  const [cancellationReason, setCancellationReason] = useState("");
  // Buffered Edit Invoice form data + reason input for the inline
  // "fill in cancellation details" prompt that appears when a user
  // changes status to 'cancelled' inside the Edit Invoice dialog
  // without supplying a reason.
  const [pendingCancellationEdit, setPendingCancellationEdit] = useState<EditInvoiceForm | null>(null);
  const [pendingCancellationReason, setPendingCancellationReason] = useState("");
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [datePaid, setDatePaid] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [sortField, setSortField] = useState<SortField>("invoiceNumber");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  // Undo/redo stack for bulk delete (soft delete is reversible via the
  // per-record restore endpoint). Only explicit-ID deletes are undoable;
  // "select all matching" deletes have no captured IDs to restore.
  const [undoStack, setUndoStack] = useState<InvoiceUndoAction[]>([]);
  const [redoStack, setRedoStack] = useState<InvoiceUndoAction[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [bulkEditField, setBulkEditField] = useState("");
  const [bulkEditValue, setBulkEditValue] = useState("");
  const [isLogEventDialogOpen, setIsLogEventDialogOpen] = useState(false);
  const [isLogCallDialogOpen, setIsLogCallDialogOpen] = useState(false);
  const [logCallNotes, setLogCallNotes] = useState("");
  const [isSetReminderDialogOpen, setIsSetReminderDialogOpen] = useState(false);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderNote, setReminderNote] = useState("");
  const [showEmailComposerModal, setShowEmailComposerModal] = useState(false);
  const [emailComposerInvoiceId, setEmailComposerInvoiceId] = useState<string | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [isSendToNewUserOpen, setIsSendToNewUserOpen] = useState(false);
  const [sendToEmail, setSendToEmail] = useState("");
  const [sendToCc, setSendToCc] = useState("");
  const [sendToSubject, setSendToSubject] = useState("");
  const [sendToBody, setSendToBody] = useState("");
  const sendToBodyEditorRef = useRef<RichTextEditorHandle>(null);
  const [isSendToScheduleOpen, setIsSendToScheduleOpen] = useState(false);
  const [isActivityDialogOpen, setIsActivityDialogOpen] = useState(false);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  
  // Resizable column widths (stored in localStorage)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem("invoices-column-widths");
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {
      invoiceNumber: 80,
      accountName: 200,
      billingPhone: 110,
      billingEmail: 150,
      dateOfEvent: 90,
      amount: 90,
      lateFee: 70,
      status: 90,
      view: 50,
      paid: 50,
      rep: 80,
      region: 100,
    };
  });
  const resizingColumn = useRef<string | null>(null);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);
  
  // Save column widths to localStorage
  useEffect(() => {
    localStorage.setItem("invoices-column-widths", JSON.stringify(columnWidths));
  }, [columnWidths]);
  
  const handleResizeStart = (e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    resizingColumn.current = columnKey;
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = columnWidths[columnKey] || 100;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizingColumn.current) return;
      const diff = e.clientX - resizeStartX.current;
      const newWidth = Math.max(50, resizeStartWidth.current + diff);
      setColumnWidths(prev => ({ ...prev, [resizingColumn.current!]: newWidth }));
    };
    
    const handleMouseUp = () => {
      resizingColumn.current = null;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };
  
  const ResizeHandle = ({ columnKey }: { columnKey: string }) => (
    <div
      className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 group-hover:bg-border"
      onMouseDown={(e) => handleResizeStart(e, columnKey)}
      onClick={(e) => e.stopPropagation()}
    />
  );

  const editForm = useForm<EditInvoiceForm>({
    defaultValues: {
      accountName: "",
      accountAddress: "",
      eventRate: "",
      discount: "0",
      invoiceDate: "",
      dateOfEvent: "",
      bookingDetails: "",
      billingContactName: "",
      billingPhoneNumber: "",
      billingEmail: "",
      status: "pending",
      lateFee: "",
      lateFeeManualOverride: false,
      paymentTerms: "due_on_completion",
      cancellationReason: "",
    },
  });

  const { data: profile } = useQuery<UserProfile & { smartSearchEnabled?: boolean }>({
    queryKey: ["/api/profile"],
  });
  const { data: aiBalance } = useQuery<{ enabled?: boolean }>({
    queryKey: ["/api/ai/balance"],
  });
  const aiEnabledForWorkspace = !!aiBalance?.enabled;
  useEffect(() => {
    if (smartHydrated) return;
    if (typeof profile?.smartSearchEnabled === "boolean") {
      setSmartEnabled(profile.smartSearchEnabled);
      setSmartHydrated(true);
    }
  }, [profile?.smartSearchEnabled, smartHydrated]);
  // Use effectiveRole when viewing as another user, otherwise use profile role
  const isAdmin = effectiveRole ? effectiveRole === "admin" : profile?.role === "admin";
  
  // Permission checking
  const { can } = useEffectivePermissions();
  const canBulkActions = can("invoices.feature.bulkActions");

  // 250ms-debounced search query so each keystroke doesn't refetch.
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchQuery(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Trigram "Did you mean…?" suggestions for the in-page search input.
  // Mirrors the sidebar quick-find behaviour (Task #293) — when fewer
  // than 3 invoices match the typed query, surface the same chip row so
  // a single typo still earns a correction prompt. Reuses the shared
  // /api/search/global hook so the React Query cache is shared across
  // every search input that hits the same query string.
  // Track the *typed* draft separately from the committed
  // `searchQuery` so the chip row reacts mid-typing without requiring
  // Enter/blur. `searchQuery` still only changes on Enter/blur so the
  // table-level filter behaviour is unchanged.
  const [searchDraft, setSearchDraft] = useState("");
  const { data: globalSuggestions } = useGlobalSearchSuggestions(
    searchDraft,
    250,
  );
  const invoiceHitCount = globalSuggestions?.items?.invoices.length ?? 0;
  const suggestionTerms = globalSuggestions?.suggestions ?? [];
  const showSuggestions =
    searchDraft.trim().length > 0 &&
    invoiceHitCount < 3 &&
    suggestionTerms.length > 0;

  // SERVER_SORTABLE — columns the storage layer can actually sort.
  // eventRate (Amount) and lateFee are decimal columns, sorted server-side
  // via a numeric-cast keyset cursor. lateFee is recalculated per-page on
  // fetch, so its ordering is eventually-consistent (a stale unpaid invoice
  // can lag until its row is next viewed); eventRate never changes after
  // fetch so its ordering is exact.
  const SERVER_SORTABLE = useMemo(
    () => new Set<SortField>([
      "invoiceNumber",
      "accountName",
      "dateOfEvent",
      "status",
      "rep",
      "eventRate",
      "lateFee",
    ]),
    [],
  );

  // Effective server sort. We send the field only if it's on the
  // whitelist; otherwise we fall through to the default ordering.
  const serverSort: string | null = SERVER_SORTABLE.has(sortField as SortField)
    ? (sortField as string)
    : null;

  // Map the multi-region UI state to the server's regionId/regionIds
  // contract: 0 → no filter, 1 → ?regionId=, >1 → ?regionIds=a,b,c.
  const regionParams = useMemo(() => {
    if (selectedRegions.length === 0) return {};
    if (selectedRegions.length === 1) return { regionId: selectedRegions[0] };
    return { regionIds: selectedRegions.join(",") };
  }, [selectedRegions]);

  const yearParam =
    selectedYear === "all" ? null : selectedYear;

  const talentParam =
    selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__"
      ? selectedTalentId
      : null;

  // Composite query key — every dimension that influences server
  // ordering or filtering is part of the key, so any change refetches
  // from page 1.
  const invoicesQueryKey = useMemo(
    () => [
      "/api/invoices",
      {
        sort: serverSort,
        dir: serverSort ? sortDirection : null,
        q: debouncedSearchQuery.trim() || null,
        year: yearParam,
        regionId: (regionParams as any).regionId ?? null,
        regionIds: (regionParams as any).regionIds ?? null,
        clientType: selectedClientType && selectedClientType !== "__unset__" ? selectedClientType : null,
        talentId: talentParam,
        smart: smartSpec ? JSON.stringify(smartSpec) : null,
      },
    ],
    [
      serverSort,
      sortDirection,
      debouncedSearchQuery,
      yearParam,
      regionParams,
      selectedClientType,
      talentParam,
      smartSpec,
    ],
  );

  const {
    data: invoicesPages,
    isLoading,
    isError: invoicesIsError,
    refetch: refetchInvoices,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<{
    items: Invoice[];
    nextCursor: string | null;
    total?: number;
  }>({
    queryKey: invoicesQueryKey,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? null,
    placeholderData: (prev) => prev,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("limit", "100");
      // Only the first page asks the server for the total record count
      // (one COUNT per filter change). Subsequent pages skip it to keep
      // scroll-pagination cheap.
      if (pageParam) {
        params.set("cursor", String(pageParam));
        params.set("skipTotal", "1");
      }
      if (serverSort) {
        params.set("sort", serverSort);
        params.set("dir", sortDirection);
      }
      if (debouncedSearchQuery.trim()) params.set("q", debouncedSearchQuery.trim());
      if (yearParam) params.set("year", yearParam);
      if ((regionParams as any).regionId) params.set("regionId", (regionParams as any).regionId);
      if ((regionParams as any).regionIds) params.set("regionIds", (regionParams as any).regionIds);
      if (talentParam) params.set("talentId", talentParam);
      if (selectedClientType && selectedClientType !== "__unset__") params.set("clientType", selectedClientType);
      if (smartSpec) {
        if (smartSpec.regionIds && smartSpec.regionIds.length > 0 && !params.has("regionId") && !params.has("regionIds")) {
          params.set("regionIds", smartSpec.regionIds.join(","));
        }
        if (smartSpec.clientType && !params.has("clientType")) {
          params.set("clientType", smartSpec.clientType);
        }
        if (smartSpec.talentId && !params.has("talentId")) {
          params.set("talentId", smartSpec.talentId);
        }
        if (typeof smartSpec.dateOfEventYear === "number" && !params.has("year")) {
          params.set("year", String(smartSpec.dateOfEventYear));
        }
        if (smartSpec.paidStatus) {
          params.set("paidStatus", smartSpec.paidStatus);
        }
        if (typeof smartSpec.billingLastContactDaysAgo === "number") {
          params.set("billingLastContactDaysAgo", String(smartSpec.billingLastContactDaysAgo));
        }
      }
      const res = await fetch(`/api/invoices?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load invoices: ${res.status}`);
      return res.json();
    },
  });

  // Flatten paginated results into the same `invoices` array name the
  // rest of this 2800-line component already consumes.
  const invoices: Invoice[] = useMemo(
    () => (invoicesPages?.pages ?? []).flatMap((p) => p.items ?? []),
    [invoicesPages],
  );
  // Total record count for the active filters. Server returns it only on
  // the first page (see queryFn), so read it off page 0.
  const totalInvoices: number | undefined = invoicesPages?.pages?.[0]?.total;

  useEffect(() => {
    if (invoicesIsError) window.dispatchEvent(new CustomEvent("clarik:query-failure"));
  }, [invoicesIsError]);

  const { data: clients = [] } = useQuery<{ id: string; regionId?: string; clientType?: string; city?: string; state?: string }[]>({
    queryKey: ["/api/clients", "lookup"],
  });

  const { data: regions = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/regions"],
  });

  const { data: users = [] } = useQuery<{ id: string; username: string; firstName?: string; lastName?: string; role?: string }[]>({
    queryKey: ["/api/users"],
  });

  const { data: clientTypes = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
  });

  const { data: defaultClientType } = useQuery<ClientTypeRecord | null>({
    queryKey: ["/api/client-types/default"],
  });

  const { data: talent = [] } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
  });

  // Task #324 — resolve the "__unset__" sentinel once talent loads: a lone
  // talent auto-selects, otherwise fall back to the workspace default talent,
  // and finally "all". Invoices keep per-invoice talent; this only defaults the
  // filter dropdown.
  useEffect(() => {
    if (selectedTalentId !== "__unset__") return;
    if (talent.length === 0) return;
    const activeTalent = talent.filter((t) => t.isActive);
    if (activeTalent.length === 1) {
      setSelectedTalentId(activeTalent[0].id);
      return;
    }
    const def = activeTalent.find((t) => t.isDefault) || talent.find((t) => t.isDefault);
    setSelectedTalentId(def ? def.id : "all");
  }, [selectedTalentId, talent]);

  const { data: lateFeeTiers = [] } = useQuery<LateFeeTier[]>({
    queryKey: ["/api/late-fee-tiers"],
    enabled: isEditDialogOpen,
  });

  const { data: lateFeeStateExceptions = [] } = useQuery<LateFeeStateException[]>({
    queryKey: ["/api/late-fee-state-exceptions"],
    enabled: isEditDialogOpen,
  });

  // Compute a human-readable late fee policy description for the selected invoice
  const lateFeeDescription = useMemo(() => {
    if (!selectedInvoice) return null;
    const lateFeeVal = parseFloat(selectedInvoice.lateFee || "0");
    if (lateFeeVal <= 0) return null;
    const clientType = (selectedInvoice as any).clientType || "schools";
    const state = (selectedInvoice as any).state;
    const matchingTier = lateFeeTiers.find((t: LateFeeTier) => t.clientType === clientType) || lateFeeTiers[0];
    if (!matchingTier) return "Payment overdue - Late fees may apply";
    const feeAmount = parseFloat(matchingTier.feeAmount);
    const graceDays = matchingTier.gracePeriodDays || 0;
    const intervalDays = matchingTier.recurringIntervalDays || 30;
    const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
    let desc = `Payment overdue - ${fmt(feeAmount)} per ${intervalDays} days after ${graceDays}-day grace period`;
    if (state) {
      const stateEx = lateFeeStateExceptions.find((e: LateFeeStateException) =>
        e.state.toLowerCase() === state.toLowerCase() &&
        e.clientType === matchingTier.clientType &&
        e.isActive
      );
      if (stateEx) {
        if (stateEx.maxFeeAmount) {
          desc += ` (capped at ${fmt(parseFloat(stateEx.maxFeeAmount))} for ${state})`;
        } else if (stateEx.maxFeePercent) {
          desc += ` (capped at ${stateEx.maxFeePercent}% for ${state})`;
        }
      }
    }
    return desc;
  }, [selectedInvoice, lateFeeTiers, lateFeeStateExceptions]);

  interface EmailTemplate {
    id: string;
    name: string;
    subject: string;
    body: string;
    bodyRich?: string | null;
    hiddenFromPickers?: boolean;
  }

  const { data: emailTemplates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/email-templates"],
    enabled: isComposeEmailOpen,
  });

  const hasMultipleTalent = talent.filter(e => e.isActive).length > 1;

  // Per-user client-type restriction. Admin/billing always see everything.
  const userAllowedClientTypeIds: string[] = useMemo(
    () => ((profile as any)?.allowedClientTypeIds as string[] | undefined) || [],
    [profile]
  );
  const userIsRestrictedByType =
    userAllowedClientTypeIds.length > 0 && profile?.role !== "admin" && profile?.role !== "billing";

  // Set default client type if not saved in localStorage, or if saved type is invalid.
  // "" is treated as a valid "All Client Types" choice for every user — including
  // restricted users, whose server-side allowed-type restriction is what limits the
  // returned invoices.
  useEffect(() => {
    if (clientTypes.length === 0) return;
    const allowedNames = userIsRestrictedByType
      ? new Set(clientTypes.filter(ct => userAllowedClientTypeIds.includes(ct.id)).map(ct => ct.name))
      : null;
    const isAllowedName = (n: string) => !allowedNames || allowedNames.has(n);

    const isAllSelected = selectedClientType === "";
    const isUnset = selectedClientType === "__unset__";
    const currentValid =
      isAllSelected ||
      (!isUnset && clientTypes.some(ct => ct.name === selectedClientType && ct.isActive) && isAllowedName(selectedClientType));

    if (currentValid) return;

    // Saved value is no longer valid (e.g. type deleted, or user lost access
    // to it). Fall-back order, matching the pre-change behaviour as closely
    // as possible:
    //   1. Workspace default client type, if it's still active and allowed
    //      for this user.
    //   2. Restricted users only: first active type they're allowed to see
    //      (preserves the legacy "always land on a concrete type" behaviour
    //      for restricted users when no workspace default is available).
    //   3. Otherwise: "All Client Types" (server-side allowed-type scope is
    //      still enforced, so this is safe for restricted users too).
    if (
      defaultClientType?.name &&
      defaultClientType.isActive &&
      isAllowedName(defaultClientType.name)
    ) {
      setSelectedClientType(defaultClientType.name);
      localStorage.setItem("invoices-selected-client-type", defaultClientType.name);
      return;
    }
    if (userIsRestrictedByType) {
      const firstAllowed = clientTypes.find(
        ct => ct.isActive && userAllowedClientTypeIds.includes(ct.id),
      );
      if (firstAllowed) {
        setSelectedClientType(firstAllowed.name);
        localStorage.setItem("invoices-selected-client-type", firstAllowed.name);
        return;
      }
    }
    setSelectedClientType("");
    localStorage.setItem("invoices-selected-client-type", "");
  }, [selectedClientType, defaultClientType, clientTypes, userIsRestrictedByType, userAllowedClientTypeIds]);

  const clientTypeOptions = useMemo(() => {
    let list = clientTypes.filter(ct => ct.isActive);
    if (userIsRestrictedByType) {
      list = list.filter(ct => userAllowedClientTypeIds.includes(ct.id));
    }
    return list
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map(ct => ct.name);
  }, [clientTypes, userIsRestrictedByType, userAllowedClientTypeIds]);

  const filteredRegions = useMemo(() => {
    if (!regionSearch.trim()) return regions;
    const search = normalizeSearchStr(regionSearch);
    return regions.filter(r => normalizeSearchStr(r.name).includes(search));
  }, [regions, regionSearch]);

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem("invoices-selected-regions", JSON.stringify(selectedRegions));
  }, [selectedRegions]);

  useEffect(() => {
    if (selectedClientType !== "__unset__") {
      localStorage.setItem("invoices-selected-client-type", selectedClientType);
    }
  }, [selectedClientType]);

  // Persist only explicit user choices — never the "__unset__" sentinel or an
  // auto-resolved default (Task #324).
  useEffect(() => {
    if (selectedTalentId === "__unset__") return;
    if (!talentPrefExplicitRef.current) return;
    localStorage.setItem("invoices-selected-talent", selectedTalentId);
  }, [selectedTalentId]);

  // Server-side cursor pagination — IntersectionObserver fires
  // fetchNextPage when the sentinel scrolls into view (rootMargin
  // 200px so we prefetch before the user actually hits the bottom).
  // Replaces the old visibleInvoicesCount + JS-slice model.
  //
  // The `root` is set to the desktop scroll container because the
  // sentinel now lives inside the virtualized table's own scroll
  // region — without this, the observer would watch the page
  // viewport, which the sentinel never enters once the body height
  // is bounded by `max-height: calc(100dvh - 280px)`.
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage
        ) {
          fetchNextPage();
        }
      },
      {
        root: scrollContainerRef.current ?? null,
        rootMargin: "200px",
      },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Handle `?view=<id>` (legacy, from the clients page) and
  // `?openInvoice=<id>` (sidebar quick-find + command palette) to
  // auto-select an invoice. We read it via wouter's `useSearch` so
  // the effect re-runs even when the user is already on /invoices
  // and only the query string changes. Note: we deliberately don't
  // gate on `!selectedInvoice` — `selectedInvoice` is sticky after
  // the dialog closes, and gating would silently swallow subsequent
  // quick-find clicks for a different invoice.
  //
  // The deep-linked invoice may be hidden by the page's current year
  // / region / client-type filter (very common for prior-year invoices
  // opened from sidebar quick-find). Rather than mutating the user's
  // filter, we fall back to fetching the invoice directly by id from
  // /api/invoices/:id when it isn't in the loaded list. If that fetch
  // 404s (deleted, wrong workspace, scoped out, bad id), we surface a
  // toast so the user gets feedback instead of being silently dropped
  // on /invoices.
  const requestedViewId = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get("openInvoice") || params.get("view");
  }, [search]);

  // Only fall back to the direct fetch when the loaded list has
  // finished loading AND doesn't contain the invoice. This avoids a
  // wasted round-trip when quick-find picks an invoice that's already
  // visible on the page, and avoids racing the list query.
  const directFetchEnabled =
    !!requestedViewId &&
    !isLoading &&
    !invoices.some((inv) => inv.id === requestedViewId);

  const {
    data: directlyFetchedInvoice,
    isFetched: directFetched,
    isFetching: directFetching,
  } = useQuery<Invoice | null>({
    queryKey: ["/api/invoices", "direct", requestedViewId],
    queryFn: async () => {
      const res = await fetch(
        `/api/invoices/${encodeURIComponent(requestedViewId!)}`,
        { credentials: "include" },
      );
      if (res.status === 404 || res.status === 403) return null;
      if (!res.ok) throw new Error(`Failed to fetch invoice: ${res.status}`);
      return res.json();
    },
    enabled: directFetchEnabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (!requestedViewId) return;
    if (selectedInvoice?.id === requestedViewId && isDialogOpen) {
      // Already showing this exact invoice — just strip the param.
      navigateLocation("/invoices", { replace: true });
      return;
    }
    if (isLoading) return;
    const invoiceToView = invoices.find((inv) => inv.id === requestedViewId);
    if (invoiceToView) {
      setSelectedInvoice(invoiceToView);
      setIsDialogOpen(true);
      navigateLocation("/invoices", { replace: true });
      return;
    }
    // Not in loaded list — wait for the direct fetch to settle before
    // deciding whether to open the dialog or show "not found".
    if (directFetching || !directFetched) return;
    if (directlyFetchedInvoice) {
      setSelectedInvoice(directlyFetchedInvoice);
      setIsDialogOpen(true);
      navigateLocation("/invoices", { replace: true });
    } else {
      toast({
        title: "Invoice not found",
        description:
          "That invoice may have been deleted or you may not have access to it.",
        variant: "destructive",
      });
      navigateLocation("/invoices", { replace: true });
    }
  }, [
    requestedViewId,
    invoices,
    selectedInvoice,
    isDialogOpen,
    isLoading,
    directlyFetchedInvoice,
    directFetched,
    directFetching,
    navigateLocation,
    toast,
  ]);

  const { data: selectedClient } = useQuery({
    queryKey: ["/api/clients", selectedInvoice?.clientId],
    queryFn: async () => {
      if (!selectedInvoice?.clientId) return null;
      const response = await fetch(`/api/clients/${selectedInvoice.clientId}`, { credentials: "include" });
      if (!response.ok) return null;
      return response.json();
    },
    enabled: !!selectedInvoice?.clientId,
  });

  // Task #494: Pull workspace-level invoice render branding so the
  // viewing dialog shows the workspace's own header / footer copy
  // rather than the legacy hard-coded agency literal. The list-rows
  // payload deliberately omits `invoiceBranding` to stay small, so we
  // fetch it once at the workspace level and pass it through to
  // <InvoiceView/>.
  const { data: invoiceRenderBranding } = useQuery<{
    invoiceTitle: string;
    companyAddress: string;
    workspaceName: string;
  }>({
    queryKey: ["/api/invoice-render-branding"],
    staleTime: 60_000,
  });

  // Year, search, region, client-type, talent, sort, dir are all
  // pushed down to the server now — `invoices` is already the
  // server-filtered, server-sorted set. Keep `yearFilteredInvoices` as
  // an alias so the rest of the (huge) component continues to compile.
  const yearFilteredInvoices = invoices;

  // Create a map for quick client lookup
  const clientMap = useMemo(() => {
    const map = new Map<string, { regionId?: string; clientType?: string; cityState?: string }>();
    clients.forEach(c => {
      const cityState = [c.city, c.state].filter(Boolean).join(", ") || undefined;
      map.set(c.id, { regionId: c.regionId, clientType: c.clientType, cityState });
    });
    return map;
  }, [clients]);

  // Create a map for quick region lookup
  const regionMap = useMemo(() => {
    const map = new Map<string, string>();
    regions.forEach(r => map.set(r.id, r.name));
    return map;
  }, [regions]);

  // Filter + sort are server-side now for most fields. For lateFee and
  // paid (not in the server whitelist) we apply a client-side sort over
  // the already-loaded pages so the user still gets useful grouping.
  const CLIENT_SORTABLE = new Set<SortField>(["paid"]);
  const sortedInvoices = CLIENT_SORTABLE.has(sortField)
    ? [...invoices].sort((a, b) => {
        let cmp = 0;
        if (sortField === "paid") {
          cmp = (a.status === "paid" ? 1 : 0) - (b.status === "paid" ? 1 : 0);
        }
        return sortDirection === "desc" ? -cmp : cmp;
      })
    : invoices;
  const visibleInvoices = sortedInvoices;
  const hasMoreInvoices = !!hasNextPage;

  // Desktop row virtualizer. Mirrors the leads / clients spreadsheet
  // pattern so behavior is consistent across spreadsheets. The
  // scroll element is the desktop scroll container declared at the
  // top of the component (`scrollContainerRef`); we render rows as
  // absolutely-positioned divs inside a relative parent sized to
  // `getTotalSize()` so the scrollbar reflects the real loaded count.
  // estimateSize 36 leaves headroom for the rep `<Select>` control
  // (taller than a plain text cell) without forcing measureElement.
  const rowVirtualizer = useVirtualizer({
    count: visibleInvoices.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 36,
    overscan: 10,
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const CLIENT_SORT_SET = new Set<SortField>(["paid"]);
  const SortIcon = ({ field }: { field: SortField }) => {
    if (!SERVER_SORTABLE.has(field) && !CLIENT_SORT_SET.has(field)) return null;
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    return sortDirection === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const emailBodyEditorRef = useRef<RichTextEditorHandle>(null);

  const handleInsertEmailVariable = (variable: string) => {
    if (emailBodyEditorRef.current) {
      emailBodyEditorRef.current.insertAtCursor(variable);
    } else {
      setEmailBody((prev) => prev + variable);
    }
  };

  const sendCustomEmailViaPreflight = useSendEmailWithPreflight();
  const [sendingCustomEmail, setSendingCustomEmail] = useState(false);

  const sendCustomEmailMutation = {
    isPending: sendingCustomEmail,
    mutate: async ({ to, subject, body, clientId, invoiceId, cc, attachments }: { to: string; subject: string; body: string; clientId?: string; invoiceId?: string; cc?: string; attachments?: StagedAttachment[] }) => {
      // Close the composer immediately; preflight + send happens in the background.
      setIsComposeEmailOpen(false);
      const recipient = to;
      const sub = subject;
      setEmailTo("");
      setEmailCc("");
      setEmailSubject("");
      setEmailBody("");
      setEmailAttachments([]);
      setSendingCustomEmail(true);
      await sendCustomEmailViaPreflight(
        {
          endpoint: "/api/email/custom",
          payload: {
            to,
            subject,
            body,
            clientId,
            invoiceId,
            cc: cc || undefined,
            attachments: attachments && attachments.length > 0
              ? attachments.map((a) => ({ filename: a.filename, contentBase64: a.contentBase64, contentType: a.contentType }))
              : undefined,
          },
          recipientEmail: recipient,
          subject: sub,
          description: "invoice email",
        },
        {
          onSent: () => {
            toast({
              title: "Email Sent",
              description: "Your email has been sent successfully.",
            });
          },
          onError: (error: any) => {
            toast({
              title: "Error",
              description: error?.message || "Failed to send email",
              variant: "destructive",
            });
          },
        },
      );
      setSendingCustomEmail(false);
    },
  } as const;

  const openConfirmationPreview = (invoiceId: string) => {
    setEmailComposerInvoiceId(invoiceId);
    setShowEmailComposerModal(true);
  };

  const markPaidMutation = useMutation({
    mutationFn: async ({ invoiceId, datePaid, paymentType, paymentNotes }: { invoiceId: string; datePaid: string; paymentType: string; paymentNotes: string }) => {
      const response = await apiRequest("PATCH", `/api/invoices/${invoiceId}`, { 
        status: "paid",
        datePaid: new Date(datePaid),
        paymentType,
        paymentNotes: paymentNotes.trim() || null,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      toast({
        title: "Invoice Marked Paid",
        description: "The invoice status has been updated to paid.",
      });
      const inv = justPaidInvoiceRef.current;
      justPaidInvoiceRef.current = null;
      closePaymentDialog();
      if (inv && ((inv as any).billingEmail || (inv as any).clientEmail)) {
        setJustPaidInvoice(inv);
        setPaymentEmailModalOpen(true);
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Update",
        description: error.message || "Could not update invoice status.",
        variant: "destructive",
      });
    },
  });

  const closePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setInvoiceToMarkPaid(null);
    setDatePaid("");
    setPaymentType("");
    setPaymentNotes("");
  };

  const openPaymentDialog = (invoice: Invoice) => {
    setInvoiceToMarkPaid(invoice);
    setDatePaid(new Date().toISOString().split("T")[0]);
    setPaymentType("");
    setPaymentNotes("");
    setPaymentDialogOpen(true);
  };

  const handlePaymentSubmit = () => {
    if (!invoiceToMarkPaid || !datePaid || !paymentType) return;
    if (paymentType === "Other" && !paymentNotes.trim()) {
      toast({
        title: "Notes Required",
        description: "Please provide notes when selecting 'Other' payment type.",
        variant: "destructive",
      });
      return;
    }
    justPaidInvoiceRef.current = invoiceToMarkPaid;
    markPaidMutation.mutate({
      invoiceId: invoiceToMarkPaid.id,
      datePaid,
      paymentType,
      paymentNotes,
    });
  };

  const cancelInvoiceMutation = useMutation({
    mutationFn: async ({ invoiceId, reason }: { invoiceId: string; reason: string }) => {
      const response = await apiRequest("PATCH", `/api/invoices/${invoiceId}`, { 
        status: "cancelled",
        cancellationReason: reason,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      toast({
        title: "Invoice Cancelled",
        description: "The invoice has been cancelled and amount set to $0.",
      });
      setInvoiceToCancel(null);
      setCancellationReason("");
      if (selectedInvoice && invoiceToCancel && selectedInvoice.id === invoiceToCancel.id) {
        setSelectedInvoice({ ...selectedInvoice, status: "cancelled", eventRate: "0" });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Cancel",
        description: error.message || "Could not cancel invoice.",
        variant: "destructive",
      });
    },
  });

  const deleteInvoiceMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      const response = await apiRequest("DELETE", `/api/invoices/${invoiceId}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      toast({
        title: "Invoice Deleted",
        description: "The invoice has been permanently deleted.",
      });
      setInvoiceToDelete(null);
      setIsDialogOpen(false);
      setSelectedInvoice(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Delete",
        description: error.message || "Could not delete invoice.",
        variant: "destructive",
      });
    },
  });

  const logCallMutation = useMutation({
    mutationFn: async ({ invoiceId, notes }: { invoiceId: string; notes: string }) => {
      const response = await apiRequest("POST", `/api/invoices/${invoiceId}/activities`, {
        type: "call",
        notes,
      });
      return response.json();
    },
    onSuccess: () => {
      setIsLogCallDialogOpen(false);
      setLogCallNotes("");
      toast({ title: "Call Logged", description: "Your call notes have been saved." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to log call.", variant: "destructive" });
    },
  });

  const setReminderMutation = useMutation({
    mutationFn: async ({ invoiceId, date, note }: { invoiceId: string; date: string; note: string }) => {
      const response = await apiRequest("POST", `/api/reminders`, {
        leadId: invoiceId,
        reminderDate: date,
        note,
      });
      return response.json();
    },
    onSuccess: () => {
      setIsSetReminderDialogOpen(false);
      setReminderDate("");
      setReminderNote("");
      toast({ title: "Reminder Set", description: "Your reminder has been saved." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to set reminder.", variant: "destructive" });
    },
  });

  const updateInvoiceMutation = useMutation({
    mutationFn: async ({ id, data, includeDatePaid }: { id: string; data: Partial<EditInvoiceForm>; includeDatePaid?: boolean }) => {
      const payload: Record<string, unknown> = { ...data };
      if (data.eventRate !== undefined) {
        payload.eventRate = data.eventRate;
      }
      if (data.discount !== undefined) {
        payload.discount = data.discount;
      }
      if (data.invoiceDate) {
        // Keep as string (YYYY-MM-DD format) - server will parse correctly
        payload.invoiceDate = data.invoiceDate;
      }
      if (data.dateOfEvent) {
        // Keep as string (YYYY-MM-DD format) - server will parse correctly to avoid timezone issues
        payload.dateOfEvent = data.dateOfEvent;
      }
      // Task #695 — Date Paid is only included in the PATCH when the
      // caller explicitly opts in (permitted user actually changed it).
      // Otherwise we strip it from the payload so unrelated edits to a
      // paid invoice don't trip the server-side editDatePaid gate.
      if (!includeDatePaid) {
        delete payload.datePaid;
      } else if (data.datePaid !== undefined) {
        payload.datePaid = data.datePaid;
      }
      // Handle lateFee - convert string to number or null
      if (data.lateFeeManualOverride !== undefined) {
        payload.lateFeeManualOverride = data.lateFeeManualOverride;
      }
      if (data.lateFee !== undefined) {
        const lateFeeNum = parseFloat(data.lateFee);
        payload.lateFee = isNaN(lateFeeNum) ? null : lateFeeNum.toString();
      }
      const response = await apiRequest("PATCH", `/api/invoices/${id}`, payload);
      return response.json();
    },
    onSuccess: (updatedInvoice) => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      toast({
        title: "Invoice Updated",
        description: "The invoice has been updated successfully.",
      });
      setIsEditDialogOpen(false);
      setSelectedInvoice(updatedInvoice);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Update",
        description: error.message || "Could not update invoice.",
        variant: "destructive",
      });
    },
  });

  const updateRepMutation = useMutation({
    mutationFn: async ({ invoiceId, repId, repName }: { invoiceId: string; repId: string | null; repName: string | null }) => {
      const response = await apiRequest("PATCH", `/api/invoices/${invoiceId}`, { 
        repId: repId,
        rep: repName,
      });
      return response.json();
    },
    onMutate: async ({ invoiceId, repId, repName }) => {
      await queryClient.cancelQueries({ queryKey: ["/api/invoices"] });
      const previousData = queryClient.getQueriesData({ queryKey: ["/api/invoices"] });
      queryClient.setQueriesData({ queryKey: ["/api/invoices"] }, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: page.items.map((inv: any) =>
              inv.id === invoiceId ? { ...inv, rep: repName, repId } : inv
            ),
          })),
        };
      });
      return { previousData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      toast({
        title: "Rep Updated",
        description: "The sales rep has been updated.",
      });
    },
    onError: (error: Error, _variables, context: any) => {
      if (context?.previousData) {
        context.previousData.forEach(([key, data]: [unknown, unknown]) => {
          queryClient.setQueryData(key as any, data);
        });
      }
      toast({
        title: "Failed to Update Rep",
        description: error.message || "Could not update the sales rep.",
        variant: "destructive",
      });
    },
  });

  const bulkEditMutation = useMutation({
    mutationFn: async ({ ids, field, value }: { ids: string[]; field: string; value: any }) => {
      const updates = ids.map(id => 
        apiRequest("PATCH", `/api/invoices/${id}`, { [field]: value })
      );
      return Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      setSelectedInvoiceIds(new Set());
      setIsBulkEditOpen(false);
      setBulkEditField("");
      setBulkEditValue("");
      toast({ title: "Invoices Updated", description: `Successfully updated ${selectedInvoiceIds.size} invoices.` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update invoices.", variant: "destructive" });
    },
  });

  const handleBulkEdit = () => {
    if (selectedInvoiceIds.size === 0 || !bulkEditField) return;
    bulkEditMutation.mutate({
      ids: Array.from(selectedInvoiceIds),
      field: bulkEditField,
      value: bulkEditValue,
    });
  };

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      // The filter set MUST mirror what the visible /api/invoices query
      // applies — including any active smart-search spec (paid/unpaid,
      // billing-last-contact age, year, etc.) — or the delete count will
      // diverge from the row count the user sees. Manual toolbar
      // selections take precedence over the spec for the same key,
      // mirroring the GET-request merge logic above.
      const yearNum =
        selectedYear && selectedYear !== "all" ? parseInt(selectedYear, 10) : null;
      const manualClientType =
        selectedClientType && selectedClientType !== "__unset__"
          ? selectedClientType
          : null;
      const manualTalentId =
        selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__"
          ? selectedTalentId
          : null;
      const manualRegionIds =
        selectedRegions.length > 0 ? selectedRegions : null;
      const manualYear =
        yearNum !== null && Number.isFinite(yearNum) ? yearNum : null;
      const body = selectAllMatching
        ? {
            action: "delete" as const,
            scope: {
              type: "filters" as const,
              filters: {
                q: debouncedSearchQuery.trim() || null,
                clientType:
                  manualClientType ?? smartSpec?.clientType ?? null,
                regionIds:
                  manualRegionIds ??
                  (smartSpec?.regionIds && smartSpec.regionIds.length > 0
                    ? smartSpec.regionIds
                    : null),
                talentId: manualTalentId ?? smartSpec?.talentId ?? null,
                dateOfEventYear:
                  manualYear ??
                  (typeof smartSpec?.dateOfEventYear === "number"
                    ? smartSpec.dateOfEventYear
                    : null),
                paidStatus: smartSpec?.paidStatus ?? null,
                billingLastContactDaysAgo:
                  typeof smartSpec?.billingLastContactDaysAgo === "number"
                    ? smartSpec.billingLastContactDaysAgo
                    : null,
              },
            },
          }
        : {
            action: "delete" as const,
            scope: { type: "ids" as const, ids: Array.from(selectedInvoiceIds) },
          };
      // Capture explicit IDs (when not selecting all matching) so the soft
      // delete can be pushed onto the undo stack and reversed via restore.
      const deletedIds = selectAllMatching ? [] : Array.from(selectedInvoiceIds);
      const response = await apiRequest("POST", "/api/invoices/bulk-action", body);
      const json = (await response.json()) as { count: number };
      return { ...json, deletedIds };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      // Bulk delete is a soft delete; push it onto the undo stack so it can be
      // reversed. "Select all matching" deletes capture no IDs and aren't undoable.
      if (result.deletedIds.length > 0) {
        setUndoStack(prev => [...prev, { type: "bulkDelete", invoiceIds: result.deletedIds }]);
        setRedoStack([]);
      }
      setSelectedInvoiceIds(new Set());
      setSelectAllMatching(false);
      setIsBulkDeleteOpen(false);
      toast({ title: "Invoices Deleted", description: `Successfully deleted ${result.count} invoices. Use Undo to restore.` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete invoices.", variant: "destructive" });
    },
  });

  const createInvoiceActivityMutation = useMutation({
    mutationFn: async ({ invoiceId, type, notes }: { invoiceId: string; type: string; notes: string }) => {
      const response = await apiRequest("POST", `/api/invoices/${invoiceId}/activities`, { type, notes });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices", selectedRowId, "activities"] });
      toast({ title: "Activity Logged", description: "Activity has been recorded." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to log activity.", variant: "destructive" });
    },
  });

  const sendToNewUserMutation = useMutation({
    mutationFn: async ({ invoiceId, email, cc, subject, body }: { invoiceId: string; email: string; cc?: string; subject: string; body: string }) => {
      const response = await apiRequest("POST", `/api/invoices/${invoiceId}/send-to-user`, { email, cc, subject, body });
      return response.json();
    },
    onSuccess: (_, variables) => {
      setIsSendToNewUserOpen(false);
      setSendToEmail("");
      setSendToCc("");
      setSendToSubject("");
      setSendToBody("");
      queryClient.invalidateQueries({ queryKey: ["/api/invoices", selectedRowId, "activities"] });
      toast({ title: "Invoice Sent", description: `Invoice sent to ${variables.email}` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to send invoice.", variant: "destructive" });
    },
  });

  const createReminderMutation = useMutation({
    mutationFn: async ({ invoiceId, dueDate, note }: { invoiceId: string; dueDate: string; note: string }) => {
      const response = await apiRequest("POST", `/api/invoices/${invoiceId}/reminders`, { dueDate, note });
      return response.json();
    },
    onSuccess: () => {
      setIsSetReminderDialogOpen(false);
      setReminderDate("");
      setReminderNote("");
      toast({ title: "Reminder Set", description: "Reminder has been scheduled." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to set reminder.", variant: "destructive" });
    },
  });

  const { data: invoiceActivities = [] } = useQuery<any[]>({
    queryKey: ["/api/invoices", selectedRowId, "activities"],
    enabled: !!selectedRowId && isActivityDialogOpen,
  });

  // Inline "Add Note" affordance inside the Activity History dialog. The
  // textarea opens beneath an "Add Note" button in the dialog header so the
  // user can drop a note onto an invoice's timeline without leaving the
  // dialog — useful for outlying clients where there's a pending action that
  // none of the structured automations capture (e.g. "still chasing
  // vendor form on this one").
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const addInvoiceNoteMutation = useMutation({
    mutationFn: async ({ invoiceId, notes }: { invoiceId: string; notes: string }) => {
      const res = await apiRequest("POST", `/api/invoices/${invoiceId}/activities`, {
        type: "note",
        notes,
      });
      return res.json();
    },
    onSuccess: (_data, variables) => {
      // Invalidate using the mutation's own invoiceId rather than
      // selectedRowId — guards against the user closing the dialog (or
      // clicking another invoice) before the mutation resolves.
      queryClient.invalidateQueries({ queryKey: ["/api/invoices", variables.invoiceId, "activities"] });
      setNewNoteText("");
      setIsAddingNote(false);
      toast({ title: "Note added", description: "The note has been logged on the activity timeline." });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not add note",
        description: error.message || "Something went wrong saving the note.",
        variant: "destructive",
      });
    },
  });

  const handleBulkDelete = () => {
    if (selectedInvoiceIds.size === 0) return;
    bulkDeleteMutation.mutate();
  };

  const handleUndo = async () => {
    if (undoStack.length === 0) return;
    const action = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    try {
      if (action.type === "bulkDelete") {
        // Bulk delete is a soft delete — restore each record by ID.
        await Promise.all(
          action.invoiceIds.map(invoiceId => apiRequest("POST", `/api/invoices/${invoiceId}/restore`)),
        );
        setRedoStack(prev => [...prev, action]);
        queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
        toast({ title: "Undone", description: `Restored ${action.invoiceIds.length} invoices.` });
      }
    } catch {
      setUndoStack(prev => [...prev, action]);
      toast({ title: "Error", description: "Failed to undo action.", variant: "destructive" });
    }
  };

  const handleRedo = async () => {
    if (redoStack.length === 0) return;
    const action = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    try {
      if (action.type === "bulkDelete") {
        await apiRequest("POST", "/api/invoices/bulk-action", {
          action: "delete",
          scope: { type: "ids", ids: action.invoiceIds },
        });
        setUndoStack(prev => [...prev, action]);
        queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
        toast({ title: "Redone", description: `Deleted ${action.invoiceIds.length} invoices again.` });
      }
    } catch {
      setRedoStack(prev => [...prev, action]);
      toast({ title: "Error", description: "Failed to redo action.", variant: "destructive" });
    }
  };

  // Clear "select all matching" whenever the underlying filters change.
  // smartSpec is included so a re-parsed sentence also resets the
  // intent — see leads-spreadsheet for the full rationale.
  useEffect(() => {
    setSelectAllMatching(false);
  }, [
    debouncedSearchQuery,
    selectedClientType,
    selectedTalentId,
    selectedRegions,
    selectedYear,
    smartSpec,
  ]);

  const handleSelectAllInvoices = (checked: boolean) => {
    if (checked) {
      // Selects only the currently-loaded pages — the user can scroll
      // to load more, then re-tick the box to grow the selection.
      // Server-backed "select all matching filters" is intentionally
      // not implemented yet; the UI label and tooltip make that clear.
      setSelectedInvoiceIds(new Set(yearFilteredInvoices.map(inv => inv.id)));
    } else {
      setSelectedInvoiceIds(new Set());
    }
  };

  const handleSelectInvoice = (invoiceId: string, checked: boolean) => {
    const newSet = new Set(selectedInvoiceIds);
    if (checked) {
      newSet.add(invoiceId);
    } else {
      newSet.delete(invoiceId);
    }
    setSelectedInvoiceIds(newSet);
  };

  const handleRowClick = (invoiceId: string) => {
    setSelectedRowId(selectedRowId === invoiceId ? null : invoiceId);
  };

  const selectedRowInvoice = selectedRowId ? sortedInvoices.find(inv => inv.id === selectedRowId) : null;

  const formatDate = (date: Date | string | null) => {
    if (!date) return "—";
    // Parse date string carefully to avoid UTC timezone shift
    // If it's a date-only string (YYYY-MM-DD), treat it as local date
    const dateStr = typeof date === 'string' ? date : date.toISOString();
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) || /^\d{4}-\d{2}-\d{2}T00:00:00/.test(dateStr);
    
    if (isDateOnly) {
      // Extract just the date parts and create date without timezone shift
      const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
      const localDate = new Date(year, month - 1, day, 12, 0, 0); // Noon to avoid DST issues
      const m = localDate.toLocaleDateString("en-US", { month: "numeric" });
      const d = localDate.toLocaleDateString("en-US", { day: "numeric" });
      const y = localDate.toLocaleDateString("en-US", { year: "2-digit" });
      return `${m}/${d}/${y}`;
    }
    
    const d = new Date(date);
    const month = d.toLocaleDateString("en-US", { timeZone: getWorkspaceTimeZone(), month: "numeric" });
    const day = d.toLocaleDateString("en-US", { timeZone: getWorkspaceTimeZone(), day: "numeric" });
    const year = d.toLocaleDateString("en-US", { timeZone: getWorkspaceTimeZone(), year: "2-digit" });
    return `${month}/${day}/${year}`;
  };

  const formatCurrency = (amount: string | number | null) => {
    if (!amount) return "$0";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(typeof amount === "string" ? parseFloat(amount) : amount);
  };

  const formatPhoneNumber = (phone: string | null | undefined) => {
    if (!phone) return "—";
    // Clean the phone number: trim, remove invisible chars, extract only digits
    const cleaned = String(phone).trim().replace(/[^\d]/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned[0] === "1") {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    // If we have 7 digits, format as local number
    if (cleaned.length === 7) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    }
    // Return original if can't format
    return phone;
  };

  const calculateLateFee = (invoice: Invoice): number => {
    if (invoice.status === "paid" || invoice.status === "cancelled") return 0;
    
    // Always use the stored lateFee value from the server
    // The server calculates late fees based on the configured tiers and timezone
    const storedFee = invoice.lateFee;
    if (storedFee === null || storedFee === undefined) return 0;
    return typeof storedFee === "string" ? parseFloat(storedFee) || 0 : storedFee;
  };

  const safeDateToString = (val: any): string => {
    if (!val) return "";
    if (typeof val === "string") return val.split("T")[0];
    try {
      const d = new Date(val);
      return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
    } catch { return ""; }
  };

  const openEditDialog = (invoice: Invoice) => {
    const dateValue = safeDateToString(invoice.dateOfEvent);
    
    const invoiceDateValue = safeDateToString(invoice.invoiceDate);
    
    editForm.reset({
      accountName: invoice.accountName || "",
      accountAddress: invoice.accountAddress || "",
      eventRate: invoice.eventRate?.toString() || "",
      discount: (invoice as any).discount?.toString() || "0",
      invoiceDate: invoiceDateValue,
      dateOfEvent: dateValue,
      bookingDetails: invoice.bookingDetails || "",
      billingContactName: (invoice as any).billingContactName || "",
      billingPhoneNumber: (invoice as any).billingPhoneNumber || "",
      billingEmail: (invoice as any).billingEmail || "",
      status: invoice.status || "pending",
      lateFee: invoice.lateFee?.toString() || "",
      lateFeeManualOverride: invoice.lateFeeManualOverride || false,
      paymentTerms: invoice.paymentTerms || "due_on_completion",
      cancellationReason: (invoice as any).cancellationReason || "",
      datePaid: safeDateToString(invoice.datePaid),
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = (data: EditInvoiceForm) => {
    if (!selectedInvoice) return;

    // If the user is changing this invoice TO 'cancelled' (and it wasn't
    // already cancelled), the server requires a cancellation reason. Rather
    // than letting the request 400 with a raw error, prompt the user with a
    // small dialog to fill in the reason — preserving every other edit they
    // made in the form. Once they confirm, the buffered data is merged with
    // the reason and sent in a single PATCH.
    const becomingCancelled =
      data.status === "cancelled" && selectedInvoice.status !== "cancelled";
    if (becomingCancelled && !data.cancellationReason?.trim()) {
      setPendingCancellationEdit(data);
      setPendingCancellationReason("");
      return;
    }

    const includeDatePaid =
      can("invoices.feature.editDatePaid") &&
      !!editForm.formState.dirtyFields.datePaid;
    updateInvoiceMutation.mutate({ id: selectedInvoice.id, data, includeDatePaid });
  };

  // Submits the buffered Edit Invoice form once the user fills in the
  // cancellation reason in the prompt dialog.
  const confirmCancellationDetails = () => {
    if (!selectedInvoice || !pendingCancellationEdit) return;
    const reason = pendingCancellationReason.trim();
    if (!reason) return;
    const includeDatePaid =
      can("invoices.feature.editDatePaid") &&
      !!editForm.formState.dirtyFields.datePaid;
    updateInvoiceMutation.mutate({
      id: selectedInvoice.id,
      data: { ...pendingCancellationEdit, cancellationReason: reason },
      includeDatePaid,
    });
    setPendingCancellationEdit(null);
    setPendingCancellationReason("");
  };

  const fetchInvoiceHtml = async (): Promise<string | null> => {
    if (!selectedInvoice) return null;
    try {
      const response = await fetch(`/api/invoices/${selectedInvoice.id}/html`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch invoice HTML");
      return await response.text();
    } catch (err) {
      toast({ title: "Error", description: "Failed to generate invoice document.", variant: "destructive" });
      return null;
    }
  };

  const handlePrint = async () => {
    const html = await fetchInvoiceHtml();
    if (!html) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const handleDownload = async () => {
    if (!selectedInvoice) return;
    try {
      toast({ title: "Generating PDF...", description: "Please wait while we prepare your invoice." });
      const response = await fetch(`/api/invoices/${selectedInvoice.id}/pdf`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to generate PDF");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Invoice-${selectedInvoice.invoiceNumber || "unknown"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: "Error", description: "Failed to generate PDF. Please try again.", variant: "destructive" });
    }
  };

  const emailPdfMutation = useMutation({
    mutationFn: async ({ invoiceId, email }: { invoiceId: string; email: string }) => {
      const response = await fetch(`/api/invoices/${invoiceId}/email-pdf`, {
        method: "POST",
        credentials: "include",
        headers: await withCsrfHeader({ "Content-Type": "application/json" }),
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const raw = data.message || "Failed to email invoice PDF";
        // Defense-in-depth: this raw fetch bypasses throwIfResNotOk, so strip
        // any HTML (e.g. Google's GFE 403 page) before it reaches the toast.
        throw new Error(sanitizeErrorBody(String(raw), response.status));
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      toast({
        title: "Invoice PDF Emailed",
        description: `Sent to ${variables.email}.`,
      });
      setEmailPdfPopoverOpen(false);
      setEmailPdfAddress("");
      // Update the open preview dialog's local snapshot in-place so the
      // "Last sent to ... at ..." header row appears immediately. The
      // selectedInvoice state is a snapshot of the row the user
      // double-clicked, so query invalidation alone wouldn't refresh it
      // until the dialog is closed and reopened.
      const sentAt = data?.sentAt ? new Date(data.sentAt) : new Date();
      setSelectedInvoice((prev) =>
        prev && prev.id === variables.invoiceId
          ? { ...prev, lastEmailedAt: sentAt, lastEmailedTo: variables.email }
          : prev,
      );
      // Refetch invoices so the list (and any other open consumer) picks
      // up the freshly-persisted lastEmailedAt/lastEmailedTo, and refetch
      // this invoice's activity feed so the rep sees the new
      // "Invoice PDF emailed to ..." entry.
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices", variables.invoiceId, "activities"] });
    },
    onError: (err: Error) => {
      toast({
        title: "Email Failed",
        description: err.message || "Failed to email the invoice PDF.",
        variant: "destructive",
      });
    },
  });

  const handleEmailPdf = () => {
    if (!selectedInvoice) return;
    const trimmed = emailPdfAddress.trim();
    if (!trimmed) return;
    emailPdfMutation.mutate({ invoiceId: selectedInvoice.id, email: trimmed });
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "paid":
        return "default";
      case "sent":
        return "secondary";
      case "pending":
        return "outline";
      case "overdue":
      case "cancelled":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getDisplayStatus = (invoice: Invoice) => {
    if (invoice.status === "paid") return "paid";
    if (invoice.status === "cancelled") return "cancelled";
    if (invoice.confirmationSentAt) return "sent";
    return "pending";
  };

  return (
    <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
      {/* Row 1: Title left, Search bar + Bulk Actions right (matches Leads layout) */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">Invoices</h1>
          <p className="text-sm text-muted-foreground hidden sm:block">
            Manage your invoices. Single-click to select a row, double-click to edit.
          </p>
        </div>
        {canBulkActions && !isMobile && (
          <BulkActionsDropdown
            entityType="invoices"
            data={invoices.map(inv => ({
              ...inv,
              amount: Math.max(0, (Number(inv.eventRate) || 0) - (Number(inv.discount) || 0))
            }))}
            fields={INVOICE_EXPORT_FIELDS}
            size="sm"
            onImportComplete={() => queryClient.invalidateQueries({ queryKey: ["/api/invoices"] })}
          />
        )}
      </div>

      {showSuggestions && (
        <RecordSuggestionChips
          suggestions={suggestionTerms}
          onSelect={(term) => {
            // Update both the typed draft (so the suggestions hook
            // re-queries with the corrected term and the chip row
            // hides if it now yields ≥3 hits) and the committed
            // searchQuery (so the table filter applies).
            setSearchQuery(term);
            setSearchDraft(term);
          }}
          containerTestId="suggestions-invoices-search"
          buttonTestIdPrefix="button-invoices-suggestion"
        />
      )}

      {/* Row 1: Saved views tab strip + search bar */}
      <SavedViewsTabStrip
        entity="invoices"
        getCurrentParams={() => ({
          sort: sortField,
          dir: sortDirection,
          year: selectedYear,
          regions: selectedRegions,
          clientType: selectedClientType === "__unset__" ? "" : selectedClientType,
          talentId: selectedTalentId,
        })}
        applyParams={(p) => {
          if (typeof p.sort === "string") setSortField(p.sort as SortField);
          if (p.dir === "asc" || p.dir === "desc") setSortDirection(p.dir);
          if (typeof p.year === "string") setSelectedYear(p.year);
          if (Array.isArray(p.regions)) setSelectedRegions(p.regions.filter((x): x is string => typeof x === "string"));
          if (typeof p.clientType === "string") setSelectedClientType(p.clientType);
          if (typeof p.talentId === "string") setSelectedTalentId(p.talentId);
        }}
        activeViewId={activeViewId}
        onActiveViewChange={setActiveViewId}
        rightSlot={
          <SmartSearchBar
            page="invoices"
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onDraftChange={setSearchDraft}
            spec={smartSpec}
            onSpecChange={setSmartSpec}
            smartEnabled={smartEnabled}
            onSmartEnabledChange={setSmartEnabled}
            aiEnabledForWorkspace={aiEnabledForWorkspace}
            placeholder="Search invoices..."
            regions={regions}
          />
        }
      />

      {/* Row 2: Filters — replaced by a Filters sheet on mobile */}
      {!isMobile && (
      <div className="border-b border-border bg-card rounded-md overflow-x-auto">
        <div className="flex items-center justify-between gap-4 p-3 sm:p-4 min-w-max">
          <div className="flex items-center gap-3">
          {/* Region Filter */}
          <Label className="text-sm font-medium">Region:</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                className="w-full sm:w-64 justify-between" 
                data-testid="select-region-filter"
              >
                <span className="truncate">
                  {selectedRegions.length === 0 
                    ? "All Regions" 
                    : selectedRegions.length === 1 
                      ? regions.find(r => r.id === selectedRegions[0])?.name || "1 region"
                      : `${selectedRegions.length} regions selected`}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0" align="start">
              <div className="p-2 border-b">
                <Input
                  placeholder="Search regions..."
                  value={regionSearch}
                  onChange={(e) => setRegionSearch(e.target.value)}
                  className="h-8"
                  data-testid="input-region-search"
                />
              </div>
              <div className="max-h-60 overflow-auto p-2 space-y-1">
                <div 
                  className="flex items-center space-x-2 p-2 rounded hover:bg-muted cursor-pointer"
                  onClick={() => setSelectedRegions([])}
                >
                  <Checkbox 
                    checked={selectedRegions.length === 0} 
                    onCheckedChange={() => setSelectedRegions([])}
                    onClick={(e) => e.stopPropagation()}
                    data-testid="checkbox-all-regions"
                  />
                  <span className="text-sm font-medium">All Regions</span>
                </div>
                {filteredRegions.map((region) => (
                  <div 
                    key={region.id}
                    className="flex items-center space-x-2 p-2 rounded hover:bg-muted cursor-pointer"
                    onClick={() => {
                      setSelectedRegions(prev => 
                        prev.includes(region.id) 
                          ? prev.filter(id => id !== region.id)
                          : [...prev, region.id]
                      );
                    }}
                  >
                    <Checkbox 
                      checked={selectedRegions.includes(region.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedRegions(prev => [...prev, region.id]);
                        } else {
                          setSelectedRegions(prev => prev.filter(id => id !== region.id));
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`checkbox-region-${region.id}`}
                    />
                    <span className="text-sm">{region.name}</span>
                  </div>
                ))}
              </div>
              {selectedRegions.length > 0 && (
                <div className="p-2 border-t">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full" 
                    onClick={() => setSelectedRegions([])}
                    data-testid="button-clear-regions"
                  >
                    Clear Selection
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* Client Type Filter */}
          <Label className="text-sm font-medium ml-4">Client Type:</Label>
          <Select
            value={selectedClientType === "" || selectedClientType === "__unset__" ? "__all__" : selectedClientType}
            onValueChange={(v) => setSelectedClientType(v === "__all__" ? "" : v)}
          >
            <SelectTrigger className="w-44" data-testid="select-client-type-filter">
              <SelectValue placeholder="All Client Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Client Types</SelectItem>
              {clientTypeOptions.map((ct) => (
                <SelectItem key={ct} value={ct}>{ct}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Talent Filter */}
          <Label className="text-sm font-medium ml-4">Talent:</Label>
          <Select
            value={selectedTalentId === "__unset__" ? "" : selectedTalentId}
            onValueChange={(value) => {
              talentPrefExplicitRef.current = true;
              setSelectedTalentId(value);
            }}
          >
            <SelectTrigger className="w-44" data-testid="select-talent-filter">
              <SelectValue placeholder="All Talent" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Talent</SelectItem>
              {talent.filter(e => e.isActive).map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name} {e.isDefault && "(Default)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Actions - right: Year selector, Undo/Redo */}
        <div className="flex items-center gap-3">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-24" data-testid="select-year">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
              <SelectItem value="2027">2027</SelectItem>
              <SelectItem value="2028">2028</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              disabled={undoStack.length === 0}
              onClick={handleUndo}
              title="Undo"
              data-testid="button-undo"
            >
              <Undo2 className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              disabled={redoStack.length === 0}
              onClick={handleRedo}
              title="Redo"
              data-testid="button-redo"
            >
              <Redo2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        </div>
      </div>
      )}

      {/* Row 3: Action toolbar — hidden on mobile (per-card detail handles these) */}
      {!isMobile && (
      <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-2 border border-border rounded-lg bg-muted/50">
        {/* Left side: Email and Log Call */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!selectedRowId || !selectedRowInvoice}
            onClick={() => {
              if (selectedRowInvoice) {
                setSelectedInvoice(selectedRowInvoice);
                const defaultEmail = (selectedRowInvoice as any).billingEmail || (selectedRowInvoice as any).clientEmail || "";
                setEmailTo(defaultEmail);
                setEmailSubject(`Invoice #${selectedRowInvoice.invoiceNumber}`);
                setEmailBody("");
                setSelectedTemplateId("");
                setIsComposeEmailOpen(true);
              }
            }}
            data-testid="button-row-email"
          >
            <Mail className="w-4 h-4 mr-1" />
            Email
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!selectedRowId || !selectedRowInvoice}
            onClick={() => {
              setLogCallNotes("");
              setIsLogCallDialogOpen(true);
            }}
            data-testid="button-row-log-call"
          >
            <Phone className="w-4 h-4 mr-1" />
            Log Call
          </Button>
        </div>
        {/* Right side: Set Reminder, Activity, Invoice Actions, + Add Invoice */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!selectedRowId || !selectedRowInvoice}
            onClick={() => {
              setReminderDate("");
              setReminderNote("");
              setIsSetReminderDialogOpen(true);
            }}
            data-testid="button-row-set-reminder"
          >
            <Bell className="w-4 h-4 mr-1" />
            Set Reminder
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!selectedRowId || !selectedRowInvoice}
            onClick={() => setIsActivityDialogOpen(true)}
            data-testid="button-row-activity"
          >
            <FileText className="w-4 h-4 mr-1" />
            Activity
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={!selectedRowId || !selectedRowInvoice} data-testid="button-invoice-actions">
                Invoice Actions
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setSelectedInvoice(selectedRowInvoice);
                  editForm.reset({
                    accountName: selectedRowInvoice.accountName || "",
                    accountAddress: selectedRowInvoice.accountAddress || "",
                    eventRate: selectedRowInvoice.eventRate?.toString() || "",
                    discount: selectedRowInvoice.discount?.toString() || "0",
                    dateOfEvent: safeDateToString(selectedRowInvoice.dateOfEvent),
                    bookingDetails: selectedRowInvoice.bookingDetails || "",
                    billingContactName: (selectedRowInvoice as any).billingContactName || "",
                    billingPhoneNumber: (selectedRowInvoice as any).billingPhoneNumber || "",
                    billingEmail: (selectedRowInvoice as any).billingEmail || "",
                    status: selectedRowInvoice.status || "pending",
                    lateFee: selectedRowInvoice.lateFee?.toString() || "",
                    lateFeeManualOverride: selectedRowInvoice.lateFeeManualOverride || false,
                    paymentTerms: selectedRowInvoice.paymentTerms || "due_on_completion",
                  });
                  setIsEditDialogOpen(true);
                }}
                data-testid="action-edit-invoice"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setSelectedInvoice(selectedRowInvoice);
                  setIsDialogOpen(true);
                }}
                data-testid="action-view-invoice"
              >
                <Eye className="w-4 h-4 mr-2" />
                Download
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setSendToEmail("");
                  setSendToSubject(`Invoice #${selectedRowInvoice.invoiceNumber}${invoiceRenderBranding?.workspaceName ? ` from ${invoiceRenderBranding.workspaceName}` : ""}`);
                  setSendToBody("");
                  setIsSendToNewUserOpen(true);
                }}
                data-testid="action-send-to-new-user"
              >
                <Send className="w-4 h-4 mr-2" />
                Send to New User
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            size="sm"
            onClick={() => setIsLogEventDialogOpen(true)}
            data-testid="button-add-invoice"
          >
            <Plus className="w-4 h-4 mr-1" />
            + Add Invoice
          </Button>
          <TutorialHint id="invoice.create" />
        </div>
      </div>
      )}

      {/* Mobile: Filters (Region / Client Type / Talent / Year) + Add Invoice */}
      {isMobile && (
        <div className="space-y-2 pb-1">
          <MobileFilterSheet
            activeCount={
              (selectedRegions.length > 0 ? 1 : 0) +
              (selectedClientType && selectedClientType !== "__unset__" ? 1 : 0) +
              (selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__" ? 1 : 0) +
              (selectedYear && selectedYear !== "all" ? 1 : 0)
            }
            chips={[
              ...(selectedRegions.length > 0
                ? [{
                    key: "regions",
                    label:
                      selectedRegions.length === 1
                        ? regions.find((r) => r.id === selectedRegions[0])?.name || "1 region"
                        : `${selectedRegions.length} regions`,
                    onRemove: () => setSelectedRegions([]),
                  }]
                : []),
              ...(selectedClientType && selectedClientType !== "__unset__"
                ? [{ key: "clientType", label: selectedClientType, onRemove: () => setSelectedClientType("") }]
                : []),
              ...(selectedTalentId && selectedTalentId !== "all" && selectedTalentId !== "__unset__"
                ? [{
                    key: "talent",
                    label: talent.find((t) => t.id === selectedTalentId)?.name || "Talent",
                    onRemove: () => setSelectedTalentId("all"),
                  }]
                : []),
              ...(selectedYear && selectedYear !== "all"
                ? [{ key: "year", label: selectedYear, onRemove: () => setSelectedYear("all") }]
                : []),
            ]}
            onClearAll={() => {
              setSelectedRegions([]);
              setSelectedClientType("");
              setSelectedTalentId("all");
              setSelectedYear("all");
            }}
          >
            <MobileFilterField label="Region">
              <MobileRegionPicker
                regions={regions}
                selected={selectedRegions}
                onChange={setSelectedRegions}
              />
            </MobileFilterField>
            <MobileFilterField label="Client Type">
              <Select
                value={selectedClientType === "" || selectedClientType === "__unset__" ? "__all__" : selectedClientType}
                onValueChange={(v) => setSelectedClientType(v === "__all__" ? "" : v)}
              >
                <SelectTrigger className="h-11" data-testid="select-mobile-client-type">
                  <SelectValue placeholder="All Client Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Client Types</SelectItem>
                  {clientTypeOptions.map((ct) => (
                    <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </MobileFilterField>
            <MobileFilterField label="Talent">
              <Select
                value={selectedTalentId === "__unset__" ? "" : selectedTalentId}
                onValueChange={(value) => {
                  talentPrefExplicitRef.current = true;
                  setSelectedTalentId(value);
                }}
              >
                <SelectTrigger className="h-11" data-testid="select-mobile-talent">
                  <SelectValue placeholder="All Talent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Talent</SelectItem>
                  {talent.filter((e) => e.isActive).map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} {e.isDefault && "(Default)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </MobileFilterField>
            <MobileFilterField label="Year">
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="h-11" data-testid="select-mobile-year">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2027">2027</SelectItem>
                  <SelectItem value="2028">2028</SelectItem>
                </SelectContent>
              </Select>
            </MobileFilterField>
          </MobileFilterSheet>

          <Button
            className="w-full h-11 justify-center gap-2"
            onClick={() => setIsLogEventDialogOpen(true)}
            data-testid="button-mobile-add-invoice"
          >
            <Plus className="w-4 h-4" />
            Add Invoice
          </Button>
        </div>
      )}

      {isLoading ? (
        <SpreadsheetSkeleton />
      ) : invoicesIsError ? (
        <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground" data-testid="state-invoices-error">
          <p className="text-sm font-medium">Failed to load invoices. Please try again.</p>
          <button
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => refetchInvoices()}
            data-testid="error-retry-button"
          >
            Retry
          </button>
        </div>
      ) : invoices.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No invoices yet"
          description="Invoices appear here after events are booked. Book a lead to generate the first one."
          className="bg-card border border-border rounded-lg"
        />
      ) : (
        <>
          {/* Bulk selection toolbar — selection is open to everyone; Bulk
              Edit/Delete are gated behind the bulkActions permission. */}
          {selectedInvoiceIds.size > 0 && (
            <div className="flex items-center gap-3 p-3 mb-4 bg-primary/10 border border-border rounded-lg flex-wrap">
              <span className="text-sm font-medium" data-testid="text-bulk-selection-count-invoices">
                {selectAllMatching
                  ? `All invoices matching current filters selected`
                  : `${selectedInvoiceIds.size} selected`}
              </span>
              {!selectAllMatching && hasNextPage && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => setSelectAllMatching(true)}
                  data-testid="button-select-all-matching-invoices"
                >
                  Select all invoices matching current filters
                </Button>
              )}
              {canBulkActions && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsBulkEditOpen(true)}
                  data-testid="button-bulk-edit-invoices"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Bulk Edit
                </Button>
              )}
              {canBulkActions && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setIsBulkDeleteOpen(true)}
                  data-testid="button-bulk-delete-invoices"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Selected
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedInvoiceIds(new Set());
                  setSelectAllMatching(false);
                }}
                data-testid="button-clear-invoice-selection"
              >
                Clear Selection
              </Button>
            </div>
          )}

          {/* Bulk Delete Confirmation Dialog */}
          <AlertDialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {selectedInvoiceIds.size} Invoices?</AlertDialogTitle>
                <AlertDialogDescription>
                  The selected invoices will be removed from your list. This can be reversed with the Undo button.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="button-cancel-bulk-delete-invoices">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleBulkDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid="button-confirm-bulk-delete-invoices"
                >
                  {bulkDeleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Delete {selectedInvoiceIds.size} Invoices
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Bulk Edit Dialog */}
          <Dialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bulk Edit {selectedInvoiceIds.size} Invoices</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Field to Update</Label>
                  <Select value={bulkEditField} onValueChange={setBulkEditField}>
                    <SelectTrigger data-testid="select-bulk-invoice-field">
                      <SelectValue placeholder="Select field" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="status">Status</SelectItem>
                      <SelectItem value="bookingDetails">Booking Details</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {bulkEditField === "status" && (
                  <div className="space-y-2">
                    <Label>New Status</Label>
                    <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                      <SelectTrigger data-testid="select-bulk-invoice-status">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {bulkEditField === "bookingDetails" && (
                  <div className="space-y-2">
                    <Label>New Booking Details</Label>
                    <Textarea
                      value={bulkEditValue}
                      onChange={(e) => setBulkEditValue(e.target.value)}
                      placeholder="Enter booking details..."
                      data-testid="input-bulk-invoice-booking-details"
                    />
                  </div>
                )}
                <Button
                  onClick={handleBulkEdit}
                  disabled={!bulkEditField || !bulkEditValue || bulkEditMutation.isPending}
                  className="w-full"
                  data-testid="button-apply-bulk-invoice-edit"
                >
                  {bulkEditMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Apply to {selectedInvoiceIds.size} Invoices
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {isMobile ? (
            <div className="space-y-2 pb-2">
              {visibleInvoices.map((invoice) => {
                const displayStatus = getDisplayStatus(invoice);
                const eventRate = Math.max(0, (Number(invoice.eventRate) || 0) - (Number(invoice.discount) || 0));
                const lateFee = calculateLateFee(invoice);
                const account = invoice.accountName || "Unnamed";
                const s = (displayStatus || "").toLowerCase();
                const statusDot =
                  s.includes("paid") ? "bg-primary"
                  : s.includes("overdue") || s.includes("failed") ? "bg-destructive"
                  : s.includes("cancel") ? "bg-muted-foreground/50"
                  : "bg-amber-500";
                return (
                  <div
                    key={invoice.id}
                    className="rounded-2xl border border-border bg-card p-4 cursor-pointer active:scale-[0.99] active:bg-foreground/[0.02] transition-transform"
                    onClick={() => { setSelectedInvoice(invoice); setIsDialogOpen(true); }}
                    data-testid={`mobile-invoice-card-${invoice.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-semibold ${bsAvatarSwatch(account)}`}>
                        {bsInitials(account)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[15px] font-semibold leading-tight text-foreground">{account}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">#{invoice.invoiceNumber}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-[15px] font-semibold tabular-nums text-foreground">{formatCurrency(eventRate)}</p>
                        <span className="mt-0.5 flex items-center justify-end gap-1.5 text-[11px] font-medium capitalize text-muted-foreground">
                          <span className={`h-1.5 w-1.5 rounded-full ${statusDot}`} />
                          {displayStatus}
                        </span>
                      </div>
                    </div>
                    {(invoice.dateOfEvent || lateFee > 0 || invoice.billingPhoneNumber || invoice.clientPhone) && (
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground/70">
                        {invoice.dateOfEvent && (
                          <span className="flex items-center gap-1.5">
                            <Clock className="h-3 w-3 shrink-0" />
                            {formatDate(invoice.dateOfEvent)}
                          </span>
                        )}
                        {lateFee > 0 && (
                          <span className="font-medium text-destructive">Late fee {formatCurrency(lateFee)}</span>
                        )}
                        {(invoice.billingPhoneNumber || invoice.clientPhone) && (
                          <a
                            href={`tel:${invoice.billingPhoneNumber || invoice.clientPhone}`}
                            className="flex items-center gap-1.5 text-foreground/80"
                            onClick={e => e.stopPropagation()}
                          >
                            <Phone className="h-3 w-3 shrink-0" />
                            {formatPhoneNumber(invoice.billingPhoneNumber || invoice.clientPhone)}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Mobile: invisible sentinel that fires fetchNextPage. */}
              {hasNextPage && (
                <div ref={loadMoreSentinelRef} className="h-8" data-testid="invoices-load-more-sentinel-mobile" />
              )}
              {isFetchingNextPage && (
                <div className="text-center text-xs text-muted-foreground py-2">Loading more invoices…</div>
              )}
            </div>
          ) : (
          <>
          {/*
            Virtualized invoices spreadsheet. Replaces the legacy
            shadcn `<Table>` so the body only renders the rows in
            (or near) the viewport. The scroll container is sized
            to a viewport-relative height so the inner virtualizer
            has a real bounded scroll element to attach to. Headers
            are a sticky CSS-grid div above the rows; rows use the
            same grid template so columns stay aligned even though
            we're no longer in a `<table>`.
          */}
          {(() => {
            const gridTemplate = [
              "40px",
              columnWidths.invoiceNumber ? `${columnWidths.invoiceNumber}px` : null,
              columnWidths.accountName ? `${columnWidths.accountName}px` : null,
              columnWidths.billingPhone ? `${columnWidths.billingPhone}px` : null,
              columnWidths.billingEmail ? `${columnWidths.billingEmail}px` : null,
              columnWidths.dateOfEvent ? `${columnWidths.dateOfEvent}px` : null,
              columnWidths.amount ? `${columnWidths.amount}px` : null,
              columnWidths.lateFee ? `${columnWidths.lateFee}px` : null,
              columnWidths.status ? `${columnWidths.status}px` : null,
              columnWidths.view ? `${columnWidths.view}px` : null,
              columnWidths.paid ? `${columnWidths.paid}px` : null,
              columnWidths.rep ? `${columnWidths.rep}px` : null,
              columnWidths.region ? `${columnWidths.region}px` : null,
            ]
              .filter(Boolean)
              .join(" ");
            const rowMinWidth = [
              40,
              columnWidths.invoiceNumber,
              columnWidths.accountName,
              columnWidths.billingPhone,
              columnWidths.billingEmail,
              columnWidths.dateOfEvent,
              columnWidths.amount,
              columnWidths.lateFee,
              columnWidths.status,
              columnWidths.view,
              columnWidths.paid,
              columnWidths.rep,
              columnWidths.region,
            ].reduce<number>((s, v) => s + (typeof v === "number" ? v : 0), 0);
            return (
              <>
              <div className="bg-card border border-card-border rounded-lg overflow-hidden print:hidden">
                <div
                  ref={scrollContainerRef}
                  className="overflow-auto"
                  style={{ maxHeight: "calc(100dvh - 280px)", minHeight: "300px" }}
                  data-testid="invoices-scroll-container"
                >
                  {/* Sticky header — same grid template as rows so columns line up. */}
                  <div
                    className="sticky top-0 z-10 bg-card border-b border-border grid text-xs font-medium text-muted-foreground"
                    style={{ gridTemplateColumns: gridTemplate, minWidth: rowMinWidth }}
                    role="row"
                  >
                    <div className="px-2 py-2 flex items-center" role="columnheader">
                      <Checkbox
                        checked={yearFilteredInvoices.length > 0 && yearFilteredInvoices.every(inv => selectedInvoiceIds.has(inv.id))}
                        onCheckedChange={handleSelectAllInvoices}
                        data-testid="checkbox-select-all-invoices"
                        title={hasNextPage ? "Select all loaded rows. More pages exist — scroll to load more, then re-tick to grow the selection." : "Select all loaded rows."}
                      />
                    </div>
                    <div className="relative group px-2 py-2 flex items-center" role="columnheader">
                      <button
                        onClick={() => toggleSort("invoiceNumber")}
                        className="flex items-center hover:text-foreground"
                        data-testid="sort-invoice-number"
                      >
                        Invoice # <SortIcon field="invoiceNumber" />
                      </button>
                      <ResizeHandle columnKey="invoiceNumber" />
                    </div>
                    <div className="relative group px-2 py-2 flex items-center" role="columnheader">
                      <button
                        onClick={() => toggleSort("accountName")}
                        className="flex items-center hover:text-foreground"
                        data-testid="sort-account-name"
                      >
                        Account <SortIcon field="accountName" />
                      </button>
                      <ResizeHandle columnKey="accountName" />
                    </div>
                    <div className="relative group px-2 py-2 flex items-center" role="columnheader">
                      Billing Phone
                      <ResizeHandle columnKey="billingPhone" />
                    </div>
                    <div className="relative group px-2 py-2 flex items-center" role="columnheader">
                      Billing Email
                      <ResizeHandle columnKey="billingEmail" />
                    </div>
                    <div className="relative group px-2 py-2 flex items-center" role="columnheader">
                      <button
                        onClick={() => toggleSort("dateOfEvent")}
                        className="flex items-center hover:text-foreground"
                        data-testid="sort-date-of-show"
                      >
                        Event Date <SortIcon field="dateOfEvent" />
                      </button>
                      <ResizeHandle columnKey="dateOfEvent" />
                    </div>
                    <div className="relative group px-2 py-2 flex items-center justify-end" role="columnheader">
                      <button
                        onClick={() => toggleSort("eventRate")}
                        className="flex items-center justify-end hover:text-foreground w-full"
                        data-testid="sort-amount"
                      >
                        Amount <SortIcon field="eventRate" />
                      </button>
                      <ResizeHandle columnKey="amount" />
                    </div>
                    <div className="relative group px-2 py-2 flex items-center justify-end" role="columnheader">
                      <button
                        onClick={() => toggleSort("lateFee")}
                        className="flex items-center hover:text-foreground ml-auto"
                        data-testid="sort-lateFee"
                      >
                        Late Fee <SortIcon field="lateFee" />
                      </button>
                      <ResizeHandle columnKey="lateFee" />
                    </div>
                    <div className="relative group px-2 py-2 flex items-center" role="columnheader">
                      <button
                        onClick={() => toggleSort("status")}
                        className="flex items-center hover:text-foreground"
                        data-testid="sort-status"
                      >
                        Status <SortIcon field="status" />
                      </button>
                      <ResizeHandle columnKey="status" />
                    </div>
                    <div className="relative group px-2 py-2 flex items-center" role="columnheader">
                      View
                      <ResizeHandle columnKey="view" />
                    </div>
                    <div className="relative group px-2 py-2 flex items-center justify-center" role="columnheader">
                      <button
                        onClick={() => toggleSort("paid")}
                        className="flex items-center hover:text-foreground"
                        data-testid="sort-paid"
                      >
                        Paid <SortIcon field="paid" />
                      </button>
                      <ResizeHandle columnKey="paid" />
                    </div>
                    <div className="relative group px-2 py-2 flex items-center" role="columnheader">
                      <button
                        onClick={() => toggleSort("rep")}
                        className="flex items-center hover:text-foreground"
                        data-testid="sort-rep"
                      >
                        Rep <SortIcon field="rep" />
                      </button>
                      <ResizeHandle columnKey="rep" />
                    </div>
                    <div className="relative group px-2 py-2 flex items-center" role="columnheader">
                      Region
                      <ResizeHandle columnKey="region" />
                    </div>
                  </div>

                  {/* Virtualized body. Outer wrapper holds the full
                      virtual height so the scroll bar reflects the
                      real loaded count; inner rows are absolutely
                      positioned and translated to their virtual y. */}
                  <div
                    style={{
                      height: rowVirtualizer.getTotalSize(),
                      width: "100%",
                      minWidth: rowMinWidth,
                      position: "relative",
                    }}
                  >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const invoice = visibleInvoices[virtualRow.index];
                      if (!invoice) return null;
                      return (
                        <div
                          key={invoice.id}
                          data-index={virtualRow.index}
                          data-testid={`row-invoice-${invoice.id}`}
                          role="row"
                          className={cn(
                            // `[&>[role=cell]]:min-w-0` is required because grid
                            // children default to `min-width: auto`, which
                            // prevents `truncate` (overflow:hidden + ellipsis)
                            // from kicking in when the column is narrow.
                            "grid items-center text-sm border-b border-border hover-elevate cursor-pointer [&>[role=cell]]:min-w-0",
                            selectedInvoiceIds.has(invoice.id) && "bg-primary/10",
                            selectedRowId === invoice.id && "bg-accent"
                          )}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            transform: `translateY(${virtualRow.start}px)`,
                            height: virtualRow.size,
                            gridTemplateColumns: gridTemplate,
                          }}
                          onClick={() => handleRowClick(invoice.id)}
                          onDoubleClick={() => {
                            setSelectedInvoice(invoice);
                            setIsDialogOpen(true);
                          }}
                        >
                          <div className="px-2 flex items-center" role="cell">
                            <Checkbox
                              checked={selectedInvoiceIds.has(invoice.id)}
                              onCheckedChange={(checked) => handleSelectInvoice(invoice.id, !!checked)}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`checkbox-invoice-${invoice.id}`}
                            />
                          </div>
                          <div className="px-2 font-medium truncate flex items-center gap-1.5" role="cell">
                            <InvoiceStatusDot
                              invoiceId={invoice.id}
                              initialStatus={{
                                depositRequired: !!(invoice as any).depositRequired,
                                depositPending:
                                  !!(invoice as any).depositRequired &&
                                  !(invoice as any).depositPaidAt,
                                hasAnyRequirement: !!(invoice as any).depositRequired,
                                allClear:
                                  !!(invoice as any).depositRequired &&
                                  !!(invoice as any).depositPaidAt,
                              }}
                            />
                            <span>#{invoice.invoiceNumber}</span>
                          </div>
                          <div className="px-2 truncate" role="cell">
                            <div className="flex flex-col gap-0">
                              <span className="truncate">{invoice.accountName || "—"}</span>
                              {clientMap.get(invoice.clientId)?.cityState && (
                                <span className="text-xs text-muted-foreground truncate">({clientMap.get(invoice.clientId)?.cityState})</span>
                              )}
                            </div>
                          </div>
                          <div className="px-2 truncate" role="cell">{formatPhoneNumber(invoice.billingPhoneNumber || invoice.clientPhone)}</div>
                          <div className="px-2 truncate" role="cell" title={invoice.billingEmail || invoice.clientEmail || "—"}>{invoice.billingEmail || invoice.clientEmail || "—"}</div>
                          <div className="px-2 truncate" role="cell">{formatDate(invoice.dateOfEvent)}</div>
                          <div className="px-2 text-right font-medium truncate" role="cell">
                            {formatCurrency(Math.max(0, (Number(invoice.eventRate) || 0) - (Number(invoice.discount) || 0)))}
                          </div>
                          <div className="px-2 text-right font-medium truncate" role="cell">
                            {(() => {
                              const lateFee = calculateLateFee(invoice);
                              if (lateFee === 0) return "—";
                              return <span className="text-destructive">{formatCurrency(lateFee)}</span>;
                            })()}
                          </div>
                          <div className="px-2 truncate" role="cell">
                            <Badge variant={getStatusVariant(getDisplayStatus(invoice))}>
                              {getDisplayStatus(invoice)}
                            </Badge>
                          </div>
                          <div className="px-2" role="cell">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedInvoice(invoice);
                                setIsDialogOpen(true);
                              }}
                              data-testid={`button-view-invoice-${invoice.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="px-2 flex items-center justify-center" role="cell">
                            <Checkbox
                              checked={invoice.status === "paid"}
                              disabled={invoice.status === "paid" || invoice.status === "cancelled"}
                              onCheckedChange={() => {
                                if (invoice.status !== "paid" && invoice.status !== "cancelled") {
                                  openPaymentDialog(invoice);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`checkbox-paid-${invoice.id}`}
                            />
                          </div>
                          <div className="px-2 text-muted-foreground truncate" role="cell" onClick={(e) => e.stopPropagation()}>
                            {isAdmin ? (
                              <Select
                                value={(() => {
                                  if (!invoice.rep) return "unassigned";
                                  const repName = invoice.rep.toLowerCase().trim();
                                  const matchingUser = users.find(u => {
                                    const fullName = `${u.firstName || ''} ${u.lastName || ''}`.trim().toLowerCase();
                                    return fullName === repName || u.username?.toLowerCase() === repName;
                                  });
                                  return matchingUser?.id || "unassigned";
                                })()}
                                onValueChange={(value) => {
                                  if (value === "unassigned") {
                                    updateRepMutation.mutate({ invoiceId: invoice.id, repId: null, repName: null });
                                  } else {
                                    const selectedUser = users.find(u => u.id === value);
                                    const repName = selectedUser
                                      ? `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || selectedUser.username
                                      : null;
                                    updateRepMutation.mutate({ invoiceId: invoice.id, repId: value, repName });
                                  }
                                }}
                              >
                                <SelectTrigger className="h-7 w-full max-w-[130px] text-xs" data-testid={`select-rep-${invoice.id}`}>
                                  <SelectValue placeholder={invoice.rep || "Select rep"} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="unassigned">Unassigned</SelectItem>
                                  {users.map((user) => (
                                    <SelectItem key={user.id} value={user.id}>
                                      {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              invoice.rep || "—"
                            )}
                          </div>
                          <div className="px-2 text-muted-foreground truncate" role="cell">
                            {(() => {
                              const client = clientMap.get(invoice.clientId);
                              const regionId = client?.regionId || invoice.regionId;
                              const regionName = regionId ? regionMap.get(regionId) || "—" : "—";
                              return <span title={regionName} className="truncate">{regionName}</span>;
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Loading-more / end-of-list footers. Live inside
                      the scroll container so they appear at the bottom
                      of the virtualized region. */}
                  {isFetchingNextPage && (
                    <div className="text-center text-xs text-muted-foreground py-2" data-testid="invoices-loading-more">
                      Loading more invoices…
                    </div>
                  )}
                  {!hasNextPage && sortedInvoices.length > 0 && (
                    <div className="text-center text-xs text-muted-foreground py-1 bg-muted/20" data-testid="invoices-all-loaded">
                      Showing all {sortedInvoices.length}
                      {typeof totalInvoices === "number" && totalInvoices !== sortedInvoices.length
                        ? ` of ${totalInvoices}`
                        : ""}{" "}
                      invoice{sortedInvoices.length === 1 ? "" : "s"}
                    </div>
                  )}
                  {hasNextPage && typeof totalInvoices === "number" && (
                    <div className="text-center text-xs text-muted-foreground py-1" data-testid="invoices-count">
                      Showing {sortedInvoices.length} of {totalInvoices} invoices
                    </div>
                  )}
                  {/* IntersectionObserver sentinel — fires fetchNextPage
                      when scrolled into view. Lives inside the scroll
                      container so it intersects with that container's
                      viewport, not the page viewport. */}
                  {hasNextPage && (
                    <div ref={loadMoreSentinelRef} className="h-8" data-testid="invoices-load-more-sentinel" />
                  )}
                </div>
              </div>

              {/* Print-only table — shown instead of the virtualized grid when printing.
                  The complex absolute-positioned grid doesn't render correctly in print
                  (columns overflow and the SelectTrigger for rep looks like a checkbox).
                  This plain <table> renders all currently-loaded invoices cleanly. */}
              <table className="hidden print:table w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-1 px-2 font-semibold">Invoice #</th>
                    <th className="text-left py-1 px-2 font-semibold">Account</th>
                    <th className="text-left py-1 px-2 font-semibold">Phone</th>
                    <th className="text-left py-1 px-2 font-semibold">Email</th>
                    <th className="text-left py-1 px-2 font-semibold">Event Date</th>
                    <th className="text-right py-1 px-2 font-semibold">Amount</th>
                    <th className="text-left py-1 px-2 font-semibold">Status</th>
                    <th className="text-left py-1 px-2 font-semibold">Rep</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedInvoices.map((invoice) => {
                    const client = clientMap.get(invoice.clientId);
                    const cityState = client?.cityState ? ` (${client.cityState})` : "";
                    const amount = invoice.eventRate != null
                      ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(invoice.eventRate) / 100)
                      : "—";
                    const eventDate = invoice.dateOfEvent
                      ? new Date(invoice.dateOfEvent + "T00:00:00").toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "2-digit" })
                      : "—";
                    return (
                      <tr key={invoice.id} className="border-b border-gray-200">
                        <td className="py-1 px-2 font-medium">#{invoice.invoiceNumber}</td>
                        <td className="py-1 px-2">{invoice.accountName || "—"}{cityState}</td>
                        <td className="py-1 px-2">{invoice.billingPhone || "—"}</td>
                        <td className="py-1 px-2">{invoice.billingEmail || "—"}</td>
                        <td className="py-1 px-2">{eventDate}</td>
                        <td className="py-1 px-2 text-right">{amount}</td>
                        <td className="py-1 px-2 capitalize">{invoice.status || "—"}</td>
                        <td className="py-1 px-2">{invoice.rep || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </>
            );
          })()}
          </>
          )}
        </>
      )}

      {/* Invoice Preview Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Invoice Preview</DialogTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleDownload} data-testid="button-download-invoice">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Popover open={emailPdfPopoverOpen} onOpenChange={(open) => {
                  setEmailPdfPopoverOpen(open);
                  if (open && selectedInvoice && !emailPdfAddress) {
                    const billingEmail = selectedInvoice.billingEmail;
                    const clientEmail = selectedClient?.emailAddress;
                    setEmailPdfAddress(billingEmail || clientEmail || "");
                  }
                }}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="button-email-pdf-invoice">
                      <Mail className="w-4 h-4 mr-2" />
                      Email PDF
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="end">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleEmailPdf();
                      }}
                      className="space-y-3"
                    >
                      <div className="space-y-1">
                        <Label htmlFor="email-pdf-address">Send PDF to</Label>
                        <Input
                          id="email-pdf-address"
                          type="email"
                          placeholder="client@example.com"
                          value={emailPdfAddress}
                          onChange={(e) => setEmailPdfAddress(e.target.value)}
                          required
                          data-testid="input-email-pdf-address"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full gap-2"
                        disabled={emailPdfMutation.isPending || !emailPdfAddress.trim()}
                        data-testid="button-send-email-pdf-invoice"
                      >
                        {emailPdfMutation.isPending ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Sending…
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4" />
                            Send PDF
                          </>
                        )}
                      </Button>
                    </form>
                  </PopoverContent>
                </Popover>
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="button-print-invoice">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </div>
            </div>
            {/* Task #241: surface the most recent successful "Email PDF"
                send (from this preview dialog OR from the client's
                self-service public-link action) so the rep can see at a
                glance that the attachment was delivered without scrolling
                down to the activity feed. */}
            {selectedInvoice?.lastEmailedAt && selectedInvoice?.lastEmailedTo && (
              <div
                className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground"
                data-testid="text-invoice-last-emailed"
              >
                <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  Last sent to{" "}
                  <span className="font-medium text-foreground" data-testid="text-invoice-last-emailed-recipient">
                    {selectedInvoice.lastEmailedTo}
                  </span>
                  {" "}at{" "}
                  <span data-testid="text-invoice-last-emailed-timestamp">
                    {new Date(selectedInvoice.lastEmailedAt).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                </span>
              </div>
            )}
            <DialogDescription className="sr-only">View and manage invoice details</DialogDescription>
          </DialogHeader>

          {/* Action Buttons */}
          {selectedInvoice && (
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-md">
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEditDialog(selectedInvoice)}
                data-testid="button-edit-invoice"
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setInvoiceToCancel(selectedInvoice)}
                disabled={selectedInvoice.status === "cancelled" || selectedInvoice.status === "paid"}
                data-testid="button-cancel-invoice"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setInvoiceToDelete(selectedInvoice)}
                data-testid="button-delete-invoice"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          )}

          {selectedInvoice && (
            <InvoiceView
              ref={invoiceRef}
              invoice={selectedInvoice}
              lateFeeDescription={lateFeeDescription}
              branding={
                (selectedInvoice as any).invoiceBranding ??
                invoiceRenderBranding ??
                null
              }
            />
          )}
          {selectedInvoice && (
            <div className="flex justify-between items-center gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  const billingEmail = (selectedInvoice as any)?.billingEmail;
                  const clientEmail = selectedClient?.emailAddress;
                  const defaultEmail = billingEmail || clientEmail || "";
                  if (defaultEmail) {
                    setEmailTo(defaultEmail);
                    setEmailSubject("");
                    setEmailBody("");
                    setSelectedTemplateId("");
                    setIsComposeEmailOpen(true);
                  } else {
                    toast({
                      title: "No Email Address",
                      description: "This client doesn't have an email address on file.",
                      variant: "destructive",
                    });
                  }
                }}
                disabled={!(selectedInvoice as any)?.billingEmail && !selectedClient?.emailAddress}
                data-testid="button-email-client"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Client
              </Button>
              <div className="flex gap-3">
                <Button
                  onClick={() => openConfirmationPreview(selectedInvoice.id)}
                  disabled={!!selectedInvoice.confirmationSentAt}
                  data-testid="button-send-confirmation-email"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {selectedInvoice.confirmationSentAt ? "Confirmation Sent" : "Email Confirmation"}
                </Button>
                {selectedInvoice.confirmationSentAt && (
                  <Button
                    variant="outline"
                    onClick={() => openConfirmationPreview(selectedInvoice.id)}
                    data-testid="button-resend-invoice-bottom"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Resend Invoice
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Details Dialog — side-by-side when the invoice has a
          deposit configured, single-pane fallback otherwise.  The new
          component handles both the deposit and balance sides and writes
          to /api/invoices/:id/deposit and /balance respectively. */}
      <InvoicePaymentDialog
        open={paymentDialogOpen}
        invoice={invoiceToMarkPaid as any}
        onOpenChange={(open) => !open && closePaymentDialog()}
        onSaved={() => {
          // Refresh the invoices list so the row reflects the new state
          // and any HOLD-clearing calendar push gets reflected.
          queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
        }}
        onBalanceSaved={() => {
          const inv = invoiceToMarkPaid;
          closePaymentDialog();
          if (inv && ((inv as any).billingEmail || (inv as any).clientEmail)) {
            setJustPaidInvoice(inv);
            setPaymentEmailModalOpen(true);
          }
        }}
      />

      {/* Cancel Invoice Confirmation Dialog */}
      <AlertDialog open={!!invoiceToCancel} onOpenChange={(open) => {
        if (!open) {
          setInvoiceToCancel(null);
          setCancellationReason("");
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel invoice #{invoiceToCancel?.invoiceNumber}? This will set the invoice amount to $0.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="cancellation-reason" className="text-sm font-medium">
              Reason for Cancellation <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="cancellation-reason"
              placeholder="Please provide a reason for cancelling this invoice..."
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              className="mt-2"
              data-testid="input-cancellation-reason"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-cancel-invoice">No, Keep Invoice</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (invoiceToCancel && cancellationReason.trim()) {
                  cancelInvoiceMutation.mutate({ invoiceId: invoiceToCancel.id, reason: cancellationReason.trim() });
                }
              }}
              disabled={cancelInvoiceMutation.isPending || !cancellationReason.trim()}
              data-testid="button-confirm-cancel-invoice"
            >
              {cancelInvoiceMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Yes, Cancel Invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancellation Details Prompt — fired by handleEditSubmit when the
          user changes status to 'cancelled' in the Edit Invoice dialog
          without supplying a reason. Buffers the rest of the form so the
          user doesn't lose unrelated edits. */}
      <AlertDialog
        open={!!pendingCancellationEdit}
        onOpenChange={(open) => {
          if (!open) {
            setPendingCancellationEdit(null);
            setPendingCancellationReason("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancellation Details Required</AlertDialogTitle>
            <AlertDialogDescription>
              You're cancelling invoice #{selectedInvoice?.invoiceNumber}. Please provide a reason — it'll be saved to the invoice record and the amount will be set to $0.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="pending-cancellation-reason" className="text-sm font-medium">
              Reason for Cancellation <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="pending-cancellation-reason"
              placeholder="e.g. Booked 2x by mistake. Invoice #500815 is current."
              value={pendingCancellationReason}
              onChange={(e) => setPendingCancellationReason(e.target.value)}
              className="mt-2"
              autoFocus
              data-testid="input-pending-cancellation-reason"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-pending-cancellation">
              Back to Edit
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancellationDetails}
              disabled={updateInvoiceMutation.isPending || !pendingCancellationReason.trim()}
              data-testid="button-confirm-pending-cancellation"
            >
              {updateInvoiceMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Save & Cancel Invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Invoice Confirmation Dialog */}
      <AlertDialog open={!!invoiceToDelete} onOpenChange={(open) => !open && setInvoiceToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete invoice #{invoiceToDelete?.invoiceNumber}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-invoice">No, Keep Invoice</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (invoiceToDelete) {
                  deleteInvoiceMutation.mutate(invoiceToDelete.id);
                }
              }}
              disabled={deleteInvoiceMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-invoice"
            >
              {deleteInvoiceMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Yes, Delete Invoice
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Edit Invoice #{selectedInvoice?.invoiceNumber}</DialogTitle>
            <DialogDescription>Update invoice details below.</DialogDescription>
          </DialogHeader>
          <form onSubmit={editForm.handleSubmit(handleEditSubmit)} className="space-y-4 overflow-y-auto flex-1 pr-2">
            <div className="space-y-2">
              <Label htmlFor="edit-accountName">Account Name</Label>
              <Input
                id="edit-accountName"
                {...editForm.register("accountName")}
                data-testid="input-edit-account-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-accountAddress">Address</Label>
              <Textarea
                id="edit-accountAddress"
                {...editForm.register("accountAddress")}
                className="min-h-[80px]"
                data-testid="input-edit-account-address"
              />
            </div>

            {/* Booking Pricing */}
            <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
              <div className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Booking</div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-eventRate">Rate ($)</Label>
                  <Input
                    id="edit-eventRate"
                    type="number"
                    step="0.01"
                    {...editForm.register("eventRate")}
                    data-testid="input-edit-show-rate"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-discount">Discount ($)</Label>
                  <Input
                    id="edit-discount"
                    type="number"
                    step="0.01"
                    min="0"
                    {...editForm.register("discount")}
                    data-testid="input-edit-discount"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Total</Label>
                  <div className="h-9 px-3 flex items-center text-sm font-semibold text-primary bg-primary/10 rounded-md border border-primary/20" data-testid="text-edit-total">
                    ${Math.max(0, parseFloat(editForm.watch("eventRate") || "0") - parseFloat(editForm.watch("discount") || "0")).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-invoiceDate">Date Booked</Label>
              <Input
                id="edit-invoiceDate"
                type="date"
                {...editForm.register("invoiceDate")}
                data-testid="input-edit-invoice-date"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-dateOfEvent">Date of Event</Label>
              <Input
                id="edit-dateOfEvent"
                type="date"
                {...editForm.register("dateOfEvent")}
                data-testid="input-edit-date-of-show"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-paymentTerms">Payment Terms</Label>
              <Select
                value={editForm.watch("paymentTerms")}
                onValueChange={(value) => editForm.setValue("paymentTerms", value)}
              >
                <SelectTrigger id="edit-paymentTerms" data-testid="select-edit-payment-terms">
                  <SelectValue placeholder="Select payment terms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="due_on_completion">Due on Completion</SelectItem>
                  <SelectItem value="net_15">Net 15</SelectItem>
                  <SelectItem value="net_30">Net 30</SelectItem>
                  <SelectItem value="net_60">Net 60</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select
                value={editForm.watch("status")}
                onValueChange={(value) => editForm.setValue("status", value)}
              >
                <SelectTrigger id="edit-status" data-testid="select-edit-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedInvoice?.status === "paid" && (
              <div className="space-y-2">
                <Label htmlFor="edit-datePaid">Date Paid</Label>
                {can("invoices.feature.editDatePaid") ? (
                  <Input
                    id="edit-datePaid"
                    type="date"
                    {...editForm.register("datePaid")}
                    data-testid="input-edit-date-paid"
                  />
                ) : (
                  <div
                    className="h-9 px-3 flex items-center text-sm rounded-md border bg-muted/30 text-muted-foreground"
                    data-testid="text-edit-date-paid-readonly"
                  >
                    {editForm.watch("datePaid") || "—"}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="edit-bookingDetails">Booking Details</Label>
              <Textarea
                id="edit-bookingDetails"
                {...editForm.register("bookingDetails")}
                className="min-h-[100px]"
                data-testid="input-edit-booking-details"
              />
            </div>

            {/* Billing Contact Section */}
            <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
              <div className="text-sm font-medium">Billing Contact</div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-billingContactName">Contact Name</Label>
                <Input
                  id="edit-billingContactName"
                  {...editForm.register("billingContactName")}
                  placeholder="Name of billing contact"
                  data-testid="input-edit-billing-contact-name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-billingPhoneNumber">Phone Number</Label>
                  <Input
                    id="edit-billingPhoneNumber"
                    {...editForm.register("billingPhoneNumber")}
                    placeholder="(555) 123-4567"
                    data-testid="input-edit-billing-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-billingEmail">Email</Label>
                  <Input
                    id="edit-billingEmail"
                    type="email"
                    {...editForm.register("billingEmail")}
                    placeholder="billing@example.com"
                    data-testid="input-edit-billing-email"
                  />
                </div>
              </div>
            </div>

            {/* Late Fee Section */}
            <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
              <div className="text-sm font-medium">Late Fees</div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-lateFeeManualOverride"
                  checked={editForm.watch("lateFeeManualOverride")}
                  onCheckedChange={(checked) => editForm.setValue("lateFeeManualOverride", !!checked, { shouldDirty: true })}
                  data-testid="checkbox-late-fee-override"
                />
                <Label htmlFor="edit-lateFeeManualOverride" className="text-sm">
                  Manual Override (disable automatic calculation)
                </Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-lateFee">Late Fee Amount ($)</Label>
                <Input
                  id="edit-lateFee"
                  type="number"
                  step="0.01"
                  min="0"
                  {...editForm.register("lateFee")}
                  disabled={!editForm.watch("lateFeeManualOverride")}
                  placeholder={editForm.watch("lateFeeManualOverride") ? "Enter late fee" : "Auto-calculated"}
                  data-testid="input-edit-late-fee"
                />
                <p className="text-xs text-muted-foreground">
                  {editForm.watch("lateFeeManualOverride") 
                    ? "Enter a custom late fee amount" 
                    : "Enable manual override to set a custom late fee"}
                </p>
              </div>
            </div>
          </form>
          <DialogFooter className="flex-shrink-0 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
              data-testid="button-cancel-edit"
            >
              Cancel
            </Button>
            <Button
              onClick={editForm.handleSubmit(handleEditSubmit)}
              disabled={updateInvoiceMutation.isPending}
              data-testid="button-save-edit"
            >
              {updateInvoiceMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compose Email Dialog */}
      <Dialog open={isComposeEmailOpen} onOpenChange={(open) => {
        setIsComposeEmailOpen(open);
        if (!open) setEmailAttachments([]);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Compose Email</DialogTitle>
            <DialogDescription>
              Send an email to {selectedClient?.contactName || selectedClient?.accountName || "the client"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email-to">To</Label>
              <Input
                id="email-to"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="Enter email address..."
                data-testid="input-email-to"
              />
            </div>
            <CcEmailInput value={emailCc} onChange={setEmailCc} data-testid="input-email-cc" />
            <div className="space-y-2">
              <Label htmlFor="template-select">Template</Label>
              <Select
                value={selectedTemplateId}
                onValueChange={(value) => {
                  setSelectedTemplateId(value);
                  if (value && value !== "none") {
                    const template = emailTemplates.find(t => t.id === value);
                    if (template) {
                      setEmailSubject(template.subject || "");
                      setEmailBody(template.bodyRich || template.body || "");
                    }
                  }
                }}
              >
                <SelectTrigger data-testid="select-email-template">
                  <SelectValue placeholder="Select a template (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template</SelectItem>
                  {/* Task #696: filter picker-hidden templates from the
                      invoice email composer. */}
                  {emailTemplates.filter((t) => !t.hiddenFromPickers).map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-subject">Subject</Label>
              <Input
                id="email-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject..."
                data-testid="input-email-subject"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="email-body">Message</Label>
                <div className="flex items-center gap-2">
                  <VoiceToTextButton onTranscript={(t) => emailBodyEditorRef.current?.insertAtCursor(t)} />
                  <EmailVariablesSelector onInsert={handleInsertEmailVariable} />
                </div>
              </div>
              <RichTextEditor
                ref={emailBodyEditorRef}
                value={emailBody}
                onChange={setEmailBody}
                placeholder="Type your message here..."
                minHeight="180px"
              />
            </div>
            <EmailAttachmentsField
              value={emailAttachments}
              onChange={setEmailAttachments}
              testIdPrefix="invoice-email-attachments"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsComposeEmailOpen(false)}
              data-testid="button-cancel-email"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsComposeEmailScheduleOpen(true)}
              disabled={!emailTo || !emailSubject || !emailBody || sendCustomEmailMutation.isPending}
              data-testid="button-schedule-compose-email"
            >
              <Clock className="w-4 h-4 mr-2" />
              Schedule send
            </Button>
            <Button
              onClick={() => {
                if (emailTo && emailSubject && emailBody) {
                  sendCustomEmailMutation.mutate({
                    to: emailTo,
                    subject: emailSubject,
                    body: emailBody,
                    cc: emailCc.trim() || undefined,
                    clientId: selectedInvoice?.clientId,
                    invoiceId: selectedInvoice?.id,
                    attachments: emailAttachments,
                  });
                }
              }}
              disabled={!emailTo || !emailSubject || !emailBody || sendCustomEmailMutation.isPending}
              data-testid="button-send-email"
            >
              {sendCustomEmailMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScheduledEmailDialog
        open={isComposeEmailScheduleOpen}
        onOpenChange={setIsComposeEmailScheduleOpen}
        sourceType="one_off"
        recipientSummary={emailTo ? `Schedule email to ${emailTo}` : undefined}
        subjectSummary={emailSubject}
        buildPayload={() => {
          if (!emailTo || !emailSubject || !emailBody) return null;
          return {
            to: emailTo,
            cc: emailCc.trim() || undefined,
            subject: emailSubject,
            htmlBody: emailBody,
            attachments: emailAttachments.length > 0
              ? emailAttachments.map((a) => ({
                  filename: a.filename,
                  contentBase64: a.contentBase64,
                  contentType: a.contentType,
                }))
              : undefined,
            invoiceId: selectedInvoice?.id,
            clientId: selectedInvoice?.clientId,
          };
        }}
        onScheduled={() => setIsComposeEmailOpen(false)}
      />

      {/* Log an Event Dialog */}
      <LogEventDialog
        open={isLogEventDialogOpen}
        onOpenChange={setIsLogEventDialogOpen}
        onSuccess={(invoice) => {
          queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
          if (invoice?.id) {
            setSelectedInvoice(invoice);
            setIsDialogOpen(true);
          }
        }}
      />

      {/* Confirmation Email Preview Dialog */}
      {emailComposerInvoiceId && (
        <EmailComposerModal
          isOpen={showEmailComposerModal}
          onClose={() => {
            setShowEmailComposerModal(false);
            setEmailComposerInvoiceId(null);
          }}
          invoiceId={emailComposerInvoiceId}
          onEmailSent={() => {
            setShowEmailComposerModal(false);
            setEmailComposerInvoiceId(null);
            queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
            // Trigger confetti celebration
            const duration = 1667;
            const animationEnd = Date.now() + duration;
            const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };
            const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;
            const interval = setInterval(() => {
              const timeLeft = animationEnd - Date.now();
              if (timeLeft <= 0) return clearInterval(interval);
              const particleCount = 50 * (timeLeft / duration);
              confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
              confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
            }, 250);
          }}
        />
      )}

      {/* Log Call Dialog */}
      <Dialog open={isLogCallDialogOpen} onOpenChange={setIsLogCallDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Call</DialogTitle>
            <DialogDescription>
              Record notes from your call with {selectedRowInvoice?.accountName || selectedInvoice?.accountName || "the client"}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Call Notes</Label>
              <Textarea
                value={logCallNotes}
                onChange={(e) => setLogCallNotes(e.target.value)}
                placeholder="Enter call notes..."
                rows={4}
                data-testid="textarea-log-call-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsLogCallDialogOpen(false)}
              data-testid="button-cancel-log-call"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const invoiceId = selectedRowId || selectedInvoice?.id;
                if (invoiceId && logCallNotes.trim()) {
                  createInvoiceActivityMutation.mutate({
                    invoiceId,
                    type: "call",
                    notes: logCallNotes,
                  });
                  setIsLogCallDialogOpen(false);
                  setLogCallNotes("");
                }
              }}
              disabled={createInvoiceActivityMutation.isPending || !logCallNotes.trim()}
              data-testid="button-save-log-call"
            >
              {createInvoiceActivityMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Reminder Dialog */}
      <Dialog open={isSetReminderDialogOpen} onOpenChange={setIsSetReminderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Reminder</DialogTitle>
            <DialogDescription>
              Set a reminder for {selectedRowInvoice?.accountName || selectedInvoice?.accountName || "this invoice"}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reminder Date</Label>
              <Input
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                data-testid="input-reminder-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
                placeholder="Reminder note..."
                rows={3}
                data-testid="textarea-reminder-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsSetReminderDialogOpen(false)}
              data-testid="button-cancel-reminder"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const invoiceId = selectedRowId || selectedInvoice?.id;
                if (invoiceId && reminderDate) {
                  createReminderMutation.mutate({
                    invoiceId,
                    dueDate: reminderDate,
                    note: reminderNote,
                  });
                }
              }}
              disabled={createReminderMutation.isPending || !reminderDate}
              data-testid="button-save-reminder"
            >
              {createReminderMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Set Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to New User Dialog */}
      <Dialog open={isSendToNewUserOpen} onOpenChange={setIsSendToNewUserOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Send Invoice to New User</DialogTitle>
            <DialogDescription>
              Send invoice #{selectedRowInvoice?.invoiceNumber} to a new email address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={sendToEmail}
                onChange={(e) => setSendToEmail(e.target.value)}
                placeholder="recipient@example.com"
                data-testid="input-send-to-email"
              />
            </div>
            <CcEmailInput value={sendToCc} onChange={setSendToCc} data-testid="input-send-to-cc" />
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={sendToSubject}
                onChange={(e) => setSendToSubject(e.target.value)}
                placeholder="Email subject..."
                data-testid="input-send-to-subject"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Message Body</Label>
                <VoiceToTextButton onTranscript={(t) => sendToBodyEditorRef.current?.insertAtCursor(t)} />
              </div>
              <RichTextEditor
                ref={sendToBodyEditorRef}
                value={sendToBody}
                onChange={setSendToBody}
                placeholder="Write your message here..."
              />
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setIsSendToNewUserOpen(false)}
              data-testid="button-cancel-send-to-user"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsSendToScheduleOpen(true)}
              disabled={
                sendToNewUserMutation.isPending ||
                !sendToEmail.trim() ||
                !sendToSubject.trim() ||
                !sendToBody.trim()
              }
              data-testid="button-schedule-send-to-user"
            >
              <Clock className="w-4 h-4 mr-2" />
              Schedule send
            </Button>
            <Button
              onClick={() => {
                if (selectedRowId && sendToEmail.trim()) {
                  sendToNewUserMutation.mutate({
                    invoiceId: selectedRowId,
                    email: sendToEmail,
                    cc: sendToCc || undefined,
                    subject: sendToSubject,
                    body: sendToBody,
                  });
                }
              }}
              disabled={sendToNewUserMutation.isPending || !sendToEmail.trim()}
              data-testid="button-send-to-user"
            >
              {sendToNewUserMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Send className="w-4 h-4 mr-2" />
              Send Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScheduledEmailDialog
        open={isSendToScheduleOpen}
        onOpenChange={setIsSendToScheduleOpen}
        sourceType="one_off"
        recipientSummary={
          sendToEmail ? `Schedule invoice send to ${sendToEmail}` : undefined
        }
        subjectSummary={sendToSubject}
        buildPayload={() => {
          if (!selectedRowId || !sendToEmail.trim() || !sendToSubject.trim() || !sendToBody.trim()) {
            return null;
          }
          return {
            to: sendToEmail.trim(),
            cc: sendToCc.trim() || undefined,
            subject: sendToSubject,
            htmlBody: sendToBody,
            invoiceId: selectedRowId,
          };
        }}
        onScheduled={() => {
          setIsSendToNewUserOpen(false);
          setSendToEmail("");
          setSendToCc("");
          setSendToSubject("");
          setSendToBody("");
        }}
      />

      {/* Activity Dialog */}
      <Dialog
        open={isActivityDialogOpen}
        onOpenChange={(open) => {
          setIsActivityDialogOpen(open);
          if (!open) {
            setIsAddingNote(false);
            setNewNoteText("");
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3 pr-8">
              <div className="min-w-0">
                <DialogTitle>Activity History</DialogTitle>
                <DialogDescription>
                  Activity timeline for invoice #{selectedRowInvoice?.invoiceNumber}
                </DialogDescription>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsAddingNote(true)}
                disabled={isAddingNote || !selectedRowId}
                className="flex-shrink-0"
                data-testid="button-add-invoice-note"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Note
              </Button>
            </div>
          </DialogHeader>

          {isAddingNote && (
            <div className="border rounded-lg p-3 bg-muted/30 space-y-2" data-testid="form-add-invoice-note">
              <Label htmlFor="new-invoice-note" className="text-sm font-medium">
                New Note
              </Label>
              <Textarea
                id="new-invoice-note"
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                placeholder="e.g. Waiting on Rochelle to send vendor form before invoicing."
                className="min-h-[80px]"
                autoFocus
                data-testid="input-new-invoice-note"
              />
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsAddingNote(false);
                    setNewNoteText("");
                  }}
                  data-testid="button-cancel-invoice-note"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    const trimmed = newNoteText.trim();
                    if (!trimmed || !selectedRowId) return;
                    addInvoiceNoteMutation.mutate({ invoiceId: selectedRowId, notes: trimmed });
                  }}
                  disabled={!newNoteText.trim() || addInvoiceNoteMutation.isPending}
                  data-testid="button-save-invoice-note"
                >
                  {addInvoiceNoteMutation.isPending && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  Save Note
                </Button>
              </div>
            </div>
          )}

          <div className="py-4">
            {invoiceActivities.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No activity recorded yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {invoiceActivities.map((activity: any, index: number) => (
                  // min-w-0 on the flex container prevents the right-side
                  // overflow that was clipping long email subjects: a flex
                  // child's default min-width is `auto`, which forces it to
                  // be at least as wide as its longest unbroken line.
                  <div key={activity.id || index} className="flex gap-3 p-3 border rounded-lg min-w-0">
                    <div className="flex-shrink-0">
                      {activity.type === "call" && <Phone className="w-5 h-5 text-blue-500" />}
                      {activity.type === "email" && <Mail className="w-5 h-5 text-green-500" />}
                      {activity.type === "note" && <FileText className="w-5 h-5 text-gray-500" />}
                      {!["call", "email", "note"].includes(activity.type) && <FileText className="w-5 h-5 text-gray-500" />}
                    </div>
                    {/* min-w-0 here too — same flexbox shrink-vs-content
                        problem as above; without it, long words inside the
                        notes block push the card past the dialog edge. */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="default" className="text-xs">{activity.type}</Badge>
                          {activity.source === "lead" && (
                            <Badge variant="outline" className="text-xs">Lead</Badge>
                          )}
                          {activity.source === "invoice" && activity.invoiceNumber && (
                            <Badge variant="outline" className="text-xs">Invoice #{activity.invoiceNumber}</Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {activity.createdAt ? new Date(activity.createdAt).toLocaleString() : ""}
                        </span>
                      </div>
                      {activity.notes && (
                        <div className="mt-1">
                          {activity.type === "email" ? (
                            <EmailActivityBody notes={activity.notes} />
                          ) : (
                            <ExpandableText text={activity.notes} className="text-sm break-words" />
                          )}
                        </div>
                      )}
                      {activity.createdByName && (
                        <p className="text-xs text-muted-foreground mt-1">by {activity.createdByName}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsActivityDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PaymentEmailModal
        open={paymentEmailModalOpen}
        onClose={() => { setPaymentEmailModalOpen(false); setJustPaidInvoice(null); }}
        invoiceId={justPaidInvoice?.id ?? null}
        clientId={justPaidInvoice?.clientId ?? null}
        clientEmail={(justPaidInvoice as any)?.billingEmail ?? (justPaidInvoice as any)?.clientEmail ?? null}
        clientName={justPaidInvoice?.accountName ?? null}
        invoiceNumber={justPaidInvoice?.invoiceNumber ?? null}
      />
    </div>
  );
}
